import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { DateRange } from "react-day-picker";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AttendanceStatus, DayHalf, LeaveType, TimeAmendmentStatus } from "@somasi/shared";
import {
  ATTENDANCE_MAX_RANGE_DAYS,
  ATTENDANCE_STATUSES,
  PAYROLL_APPROVE_ROLES,
  REMOTE_WORKING_STATUSES,
  WORKING_ATTENDANCE_STATUSES,
  amendmentProblem,
  describeAmendment,
  halvesToDays,
  instantAt,
  leaveTypeColour,
  timeOfDay,
  withLeaveColours,
} from "@somasi/shared";
import {
  api,
  type AttendanceGridDay,
  type AttendanceGridRow,
  type TimeAmendmentRow,
  type TimesheetDay,
  type TimesheetEntry,
  type TimesheetRow,
} from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { SettleUnansweredDialog } from "@/components/settle-unanswered-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AttendancePersonSelect } from "@/components/entity-select";
import {
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  CircleDollarSign,
  Clock,
  HelpCircle,
  Palmtree,
  Info,
  Lock,
  Pencil,
  RefreshCw,
  StickyNote,
  Timer,
  Users,
} from "lucide-react";
import { StatCard } from "@/components/stat-card";
import {
  FilterSelect,
  TableMessageRow,
  TablePagination,
  usePagination,
} from "@/components/list-controls";
import { Truncated } from "@/components/truncated";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import { NOTIFICATIONS_KEY } from "@/hooks/use-notifications";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/attendance")({
  /**
   * Which view is open, in the URL — so a notification about an unsettled
   * clocked entry lands on Hours rather than on the calendar, and so a view can
   * be linked to and survives a reload. An unrecognised value falls back to the
   * calendar instead of being refused: a stale link should still open the page.
   */
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search.tab === "string" && search.tab ? { tab: search.tab } : {},
  head: () => ({
    meta: [
      { title: "Attendance — Somasi Ops" },
      {
        name: "description",
        content:
          "One person's month as a calendar, the whole team as a grid, and the hours behind both.",
      },
    ],
  }),
  component: AttendancePage,
});

/**
 * Attendance, read two ways.
 *
 * **One person's calendar** is what somebody opens about themselves: a month
 * laid out as a month, with the public holidays named, the leave coloured by
 * its own type, and every day split into a morning and an afternoon — because
 * half a day is the smallest thing this business books and a calendar that
 * cannot show it is a calendar that rounds somebody's leave up.
 *
 * **The team grid** is the worksheet it replaces: people down the side, dates
 * across the top, a letter in each cell. Whoever runs the payroll reads that
 * one; an employee has no business in it, so it is only offered to somebody
 * who can see more than themselves.
 *
 * **Hours** is the same window in minutes rather than half-days, which is a
 * different question: the grid says whether somebody was working, the
 * timesheet says for how long.
 *
 * All three read the same period, chosen once — a calendar month, or a date
 * range for the stretch that does not line up with one.
 *
 * The rule the whole thing turns on: an `unconfirmed` half is a question put to
 * a person, drawn as a question mark and never as an absence. A pay period
 * cannot close while one remains.
 */

/**
 * A day on screen, which is a half-day status plus one case the stored statuses
 * have no room for.
 *
 * `upcoming` is next Tuesday. Nothing is recorded for it and nothing should be:
 * the proposal calls it `unconfirmed` because that is what an empty working day
 * is, but "not yet answered" is a thing you can only say about a day that has
 * happened. Asking somebody to account for a day in the future is noise, and in
 * a calendar — which is mostly future — it would be most of the screen.
 */
type TileStatus = AttendanceStatus | "upcoming";

/**
 * How each status is drawn.
 *
 * `code` and `className` are the dense team grid, following the worksheet's own
 * legend colours. `short` and `tileClassName` are the calendar, where a cell is
 * wide enough for a word and a letter would be a puzzle.
 */
const STATUS_STYLE: Record<
  TileStatus,
  { code: string; short: string; label: string; className: string; tileClassName: string }
> = {
  office: {
    code: "",
    short: "Office",
    label: "In the office",
    className: "bg-transparent",
    tileClassName: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  },
  wfh: {
    code: "W",
    short: "Home",
    label: "Work from home",
    className: "bg-amber-400 text-amber-950",
    tileClassName: "bg-amber-400 text-amber-950",
  },
  client_site: {
    code: "CS",
    short: "Client",
    label: "At a client",
    className: "bg-sky-200 text-sky-950 dark:bg-sky-900/50 dark:text-sky-100",
    tileClassName: "bg-sky-200 text-sky-950 dark:bg-sky-900/50 dark:text-sky-100",
  },
  travel: {
    code: "TR",
    short: "Travel",
    label: "Travelling",
    className: "bg-sky-200 text-sky-950 dark:bg-sky-900/50 dark:text-sky-100",
    tileClassName: "bg-sky-200 text-sky-950 dark:bg-sky-900/50 dark:text-sky-100",
  },
  training: {
    code: "TN",
    short: "Training",
    label: "Training",
    className: "bg-sky-200 text-sky-950 dark:bg-sky-900/50 dark:text-sky-100",
    tileClassName: "bg-sky-200 text-sky-950 dark:bg-sky-900/50 dark:text-sky-100",
  },
  on_call: {
    code: "OC",
    short: "On call",
    label: "On call",
    className: "bg-sky-200 text-sky-950 dark:bg-sky-900/50 dark:text-sky-100",
    tileClassName: "bg-sky-200 text-sky-950 dark:bg-sky-900/50 dark:text-sky-100",
  },
  leave: {
    code: "L",
    short: "Leave",
    label: "On leave",
    className: "bg-emerald-500 text-white",
    tileClassName: "bg-emerald-500 text-white",
  },
  holiday: {
    code: "B",
    short: "Holiday",
    label: "Public holiday",
    className: "bg-purple-600 text-white",
    tileClassName: "bg-purple-600 text-white",
  },
  // A weekend needs no words. The date number alone says it, and a calendar
  // that labels every Saturday "not a working day" is a calendar of labels.
  non_working: {
    code: "",
    short: "",
    label: "Not a working day",
    className: "bg-muted/60",
    tileClassName: "bg-muted",
  },
  absent_unauthorised: {
    code: "A",
    short: "Absent",
    label: "Unauthorised absence",
    className: "bg-red-600 text-white",
    tileClassName: "bg-red-600 text-white",
  },
  unconfirmed: {
    code: "?",
    short: "Not answered",
    label: "Not yet answered",
    className:
      "bg-orange-100 text-orange-900 ring-1 ring-inset ring-orange-400 dark:bg-orange-950/40 dark:text-orange-200 dark:ring-orange-500/60",
    tileClassName:
      "bg-orange-100 text-orange-900 ring-1 ring-inset ring-orange-400 dark:bg-orange-950/40 dark:text-orange-200 dark:ring-orange-500/60",
  },
  upcoming: {
    code: "",
    short: "",
    label: "A working day still to come",
    className: "bg-muted/30",
    tileClassName: "border border-dashed bg-background",
  },
};

/**
 * The statuses whose colour is strong enough for a tooltip to be drawn in it.
 *
 * A day of working from home is amber on the calendar and was a teal box when
 * you hovered it, which made the panel look like it belonged to some other day.
 * These are the fills that carry their own text contrast — the amber of home
 * and the red of an unauthorised absence. Everything else stays on the default:
 * the pale statuses would swallow the text, and a holiday and a day of leave
 * take their colour from the holiday itself and from the leave type's own row.
 */
const FILLED_TOOLTIP_STATUSES: readonly TileStatus[] = ["wfh", "absent_unauthorised"];

/** The first of a month, as `YYYY-MM-DD`. */
function monthStart(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-01`;
}

function monthEnd(year: number, month: number): string {
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
}

/** Today, in the same UTC dates every stored day is keyed by. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** How many days a window covers, both ends included. */
function spanDays(from: string, to: string): number {
  return (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000 + 1;
}

/** Every calendar month the window touches, in order, so a range draws as months. */
function monthsBetween(from: string, to: string): { year: number; month: number }[] {
  const months: { year: number; month: number }[] = [];
  const last = new Date(`${to}T00:00:00Z`);
  const cursor = new Date(`${from.slice(0, 7)}-01T00:00:00Z`);

  while (
    cursor.getUTCFullYear() < last.getUTCFullYear() ||
    (cursor.getUTCFullYear() === last.getUTCFullYear() &&
      cursor.getUTCMonth() <= last.getUTCMonth())
  ) {
    months.push({ year: cursor.getUTCFullYear(), month: cursor.getUTCMonth() });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return months;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAY_INITIALS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * What a half-day is, allowing for the fact that some of them have not happened.
 *
 * Only ever softens an open question, and only on a date after today: "not yet
 * answered" is a thing you can say about a day that has happened, and nothing
 * else is touched. Every question mark on or before today stays exactly as it
 * stands, which is what keeps the ones that block a pay period visible.
 *
 * It does not matter who wrote the row. A rollup that ran across a whole month
 * stores `unconfirmed` against next Tuesday as readily as it does against last
 * Tuesday, so reading the stored row for a future date would put the question
 * mark straight back — which is why the test is the date alone and not whether
 * the day is still a proposal.
 */
function effectiveStatus(day: AttendanceGridDay, half: DayHalf, asOf: string): TileStatus {
  const status = day[`${half}_status`] as AttendanceStatus;
  if (status === "unconfirmed" && day.date > asOf) return "upcoming";
  return status;
}

function leaveTypeOf(day: AttendanceGridDay, half: DayHalf): string | null {
  return day[`${half}_leave_type_id`];
}

/** Colour for one half, preferring the leave type's own over the generic green. */
function halfTone(
  status: TileStatus,
  leaveTypeId: string | null,
  typeById: Map<string, LeaveType>,
  variant: "grid" | "tile",
): { className: string; style: { backgroundColor: string } | undefined } {
  const style = STATUS_STYLE[status];

  if (status === "leave") {
    const type = leaveTypeId ? typeById.get(leaveTypeId) : undefined;
    if (type) {
      return { className: "text-white", style: { backgroundColor: leaveTypeColour(type) } };
    }
  }
  return {
    className: variant === "grid" ? style.className : style.tileClassName,
    style: undefined,
  };
}

/** The grid's letter — a leave type's own code where it has one. */
function halfCode(
  status: TileStatus,
  leaveTypeId: string | null,
  typeById: Map<string, LeaveType>,
): string {
  if (status === "leave") {
    return leaveTypeId ? (typeById.get(leaveTypeId)?.code ?? "L") : "L";
  }
  return STATUS_STYLE[status].code;
}

/** The calendar's word. Empty for the statuses a date number already explains. */
function halfShort(
  status: TileStatus,
  leaveTypeId: string | null,
  typeById: Map<string, LeaveType>,
): string {
  if (status === "leave") {
    return leaveTypeId ? (typeById.get(leaveTypeId)?.name ?? "Leave") : "Leave";
  }
  return STATUS_STYLE[status].short;
}

/** The whole name, for a tooltip and for anywhere a mistake would cost money. */
function halfName(
  status: TileStatus,
  leaveTypeId: string | null,
  typeById: Map<string, LeaveType>,
): string {
  if (status === "leave") {
    return leaveTypeId ? (typeById.get(leaveTypeId)?.name ?? "Leave") : "Leave";
  }
  return STATUS_STYLE[status].label;
}

/** Half-day counts over a window, as the summary panel reads them. */
interface PeriodTally {
  byStatus: Partial<Record<TileStatus, number>>;
  byLeaveType: Record<string, number>;
}

/**
 * Counts the halves in a window.
 *
 * Done here rather than taken from the server's own totals because this one
 * knows about `upcoming`: the server counts an empty future working day as an
 * open question, which is right for the rollup and wrong for a person reading
 * next month. The money figures — `unpaid_halves` — still come from the server,
 * because a partially paid type makes them fractional and that arithmetic
 * belongs in one place.
 */
function tallyHalves(days: readonly AttendanceGridDay[], asOf: string): PeriodTally {
  const tally: PeriodTally = { byStatus: {}, byLeaveType: {} };

  for (const day of days) {
    for (const half of ["am", "pm"] as const) {
      const status = effectiveStatus(day, half, asOf);
      tally.byStatus[status] = (tally.byStatus[status] ?? 0) + 1;

      if (status === "leave") {
        const typeId = leaveTypeOf(day, half);
        if (typeId) tally.byLeaveType[typeId] = (tally.byLeaveType[typeId] ?? 0) + 1;
      }
    }
  }
  return tally;
}

/** Which period the three views read. A calendar month, or an arbitrary stretch. */
type PeriodMode = "month" | "range";

/**
 * What is wrong with a hand-typed range, in words, before a request is sent.
 *
 * The server refuses the same three things; saying so here is the difference
 * between a date half-typed and an error toast.
 */
function rangeProblem(from: string, to: string): string | null {
  if (!from || !to) return "Pick both a start and an end date.";
  if (to < from) return "The end date is before the start date.";
  if (spanDays(from, to) > ATTENDANCE_MAX_RANGE_DAYS) {
    return `A range may cover at most ${ATTENDANCE_MAX_RANGE_DAYS} days.`;
  }
  return null;
}

/** The views this screen has, and the one a link with no `?tab=` lands on. */
const ATTENDANCE_TABS = ["calendar", "grid", "hours", "corrections"] as const;
const DEFAULT_ATTENDANCE_TAB = "calendar";

/**
 * The open view, held in the URL rather than in state.
 *
 * Controlled by the search parameter both ways, so a link to `?tab=hours`
 * switches the view even when the page is already open. Switching by hand
 * replaces the history entry — Back should leave the page rather than walk
 * through the views looked at on the way.
 */
function useAttendanceTab() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const value = ATTENDANCE_TABS.includes(tab as (typeof ATTENDANCE_TABS)[number])
    ? (tab as string)
    : DEFAULT_ATTENDANCE_TAB;

  return {
    value,
    onChange: (next: string) => {
      void navigate({ search: { tab: next }, replace: true });
    },
  };
}

function AttendancePage() {
  const { hasAny } = useAuth();
  const qc = useQueryClient();
  const canApprove = hasAny(PAYROLL_APPROVE_ROLES);

  const now = new Date();
  const [mode, setMode] = useState<PeriodMode>("month");
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth());
  const [rangeFrom, setRangeFrom] = useState(monthStart(now.getUTCFullYear(), now.getUTCMonth()));
  const [rangeTo, setRangeTo] = useState(monthEnd(now.getUTCFullYear(), now.getUTCMonth()));
  const [chosen, setChosen] = useState("");
  const { value: view, onChange: setView } = useAttendanceTab();
  const [editing, setEditing] = useState<{ row: AttendanceGridRow; day: AttendanceGridDay } | null>(
    null,
  );

  const from = mode === "month" ? monthStart(year, month) : rangeFrom;
  const to = mode === "month" ? monthEnd(year, month) : rangeTo;
  const problem = mode === "range" ? rangeProblem(rangeFrom, rangeTo) : null;

  /*
   * Whose attendance this caller may open, asked once and without a search
   * term — so `total` is the count of people they can see at all. One means
   * themselves, which is the whole difference between the two audiences: no
   * picker, and no team grid to read.
   */
  const people = useQuery({
    queryKey: ["attendance_people", "visible"],
    queryFn: async () => {
      const result = await api.attendance.people({ limit: 1 });
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });

  const canChoose = (people.data?.total ?? 0) > 1;
  const employeeId = chosen || people.data?.me || "";
  /** Whether the person on screen is the caller, which several wordings turn on. */
  const isSelf = !!employeeId && employeeId === people.data?.me;

  const types = useQuery({
    queryKey: ["leave_types_all"],
    queryFn: async () => {
      const result = await api.leaveTypes.list().order("sort_order");
      if (result.error) throw new Error(result.error.message);
      /*
       * Colours resolved here, once, rather than at each swatch: a type with
       * none of its own gets one nothing else is using, so the legend never
       * shows two kinds of leave as the same square.
       */
      return withLeaveColours((result.data ?? []) as LeaveType[]);
    },
  });

  const typeById = useMemo(() => new Map((types.data ?? []).map((t) => [t.id, t])), [types.data]);

  /*
   * The window the mark-days dialog is open on, if it is. Filling in from logins
   * leaves behind exactly the days nobody's activity could answer, so it opens
   * straight onto them rather than leaving somebody to find each one on the grid.
   */
  const [settling, setSettling] = useState<{
    from: string;
    to: string;
    label: string;
    employeeId?: string;
  } | null>(null);

  const rollup = useMutation({
    mutationFn: async () => {
      const result = await api.attendance.rollup(from, to);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (data) => {
      const open = data?.open_questions ?? 0;
      notify.success(
        `Filled in ${data?.days_proposed ?? 0} day(s). ` +
          `${open} half-day(s) still need an answer.`,
      );
      void qc.invalidateQueries({ queryKey: ["attendance"] });

      // Calendar and Hours are about one person, picked above, so the dialog is
      // too. Team grid and Time edits pick nobody, so theirs lists everybody.
      // Opened only when there is something to answer, never onto an empty list.
      const person =
        (view === "calendar" || view === "hours") && employeeId ? employeeId : undefined;
      const theirs = person
        ? (data?.questions ?? []).some((q) => q.employee_id === person)
        : open > 0;
      if (theirs) {
        setSettling({
          from,
          to,
          label:
            mode === "month"
              ? `${MONTH_NAMES[month]} ${year}`
              : `${dayLabel(from)} – ${dayLabel(to)}`,
          employeeId: person,
        });
      }
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const step = (by: number) => {
    const next = new Date(Date.UTC(year, month + by, 1));
    setYear(next.getUTCFullYear());
    setMonth(next.getUTCMonth());
  };

  const goToday = () => {
    const today = new Date();
    setYear(today.getUTCFullYear());
    setMonth(today.getUTCMonth());
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* The clock is a status and one button, so it sits with the page's own
          actions rather than taking a row of its own above the tabs. */}
      <PageHeader
        title="Attendance"
        description="Who worked where, who was away, and what still needs an answer."
        actions={
          <>
            <ClockControl />
            {canApprove && (
              <Button
                variant="outline"
                onClick={() => rollup.mutate()}
                disabled={rollup.isPending || !!problem}
              >
                <RefreshCw className={cn("mr-2 h-4 w-4", rollup.isPending && "animate-spin")} />
                Fill in from logins
              </Button>
            )}
          </>
        }
      />

      <Tabs value={view} onValueChange={setView} className="flex min-h-0 flex-1 flex-col gap-4">
        <TabsList className="shrink-0 self-start">
          <TabsTrigger value="calendar" className="gap-2">
            <CalendarDays className="h-3.5 w-3.5" />
            Calendar
          </TabsTrigger>
          {/* Only offered to somebody who can see more than themselves: a grid of
              one row is a worse calendar, and nobody else's row is theirs to read. */}
          {canChoose && (
            <TabsTrigger value="grid" className="gap-2">
              <Users className="h-3.5 w-3.5" />
              Team grid
            </TabsTrigger>
          )}
          <TabsTrigger value="hours" className="gap-2">
            <Timer className="h-3.5 w-3.5" />
            Hours
          </TabsTrigger>
          {/* Corrections to clocked times: what somebody has asked for, and
              what an admin still has to decide. Beside the timesheet they are
              about, because that is the only place they mean anything. */}
          <TabsTrigger value="corrections" className="gap-2">
            <Pencil className="h-3.5 w-3.5" />
            Time edits
          </TabsTrigger>
        </TabsList>

        {/*
         * One period, chosen once, read by all three views — and the person
         * alongside it, on the two views that are about one person. The team
         * grid is every row by definition, so the picker is not shown with it
         * rather than shown and ignored.
         */}
        {/* Time edits is a queue, not a period, so the period is not offered there. */}
        {view !== "corrections" && (
          <PeriodControls
            mode={mode}
            onModeChange={setMode}
            year={year}
            month={month}
            onYear={setYear}
            onMonth={setMonth}
            onStep={step}
            onToday={goToday}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            onRangeFrom={setRangeFrom}
            onRangeTo={setRangeTo}
            problem={problem}
            person={
              canChoose && view !== "grid" ? (
                <AttendancePersonSelect
                  value={employeeId}
                  onChange={setChosen}
                  className="w-full sm:w-64"
                />
              ) : null
            }
          />
        )}

        <TabsContent value="calendar" className="mt-0 min-h-0 flex-1 space-y-6 overflow-y-auto">
          {problem ? (
            <PeriodUnset />
          ) : (
            <PersonCalendar
              from={from}
              to={to}
              employeeId={employeeId}
              canChoose={canChoose}
              settling={people.isPending}
              types={types.data ?? []}
              typeById={typeById}
              onPick={(row, day) => setEditing({ row, day })}
            />
          )}
        </TabsContent>

        {canChoose && (
          <TabsContent value="grid" className="mt-0 min-h-0 flex-1 space-y-6 overflow-y-auto">
            {problem ? (
              <PeriodUnset />
            ) : (
              <TeamGrid
                from={from}
                to={to}
                types={types.data ?? []}
                typeById={typeById}
                onPick={(row, day) => setEditing({ row, day })}
              />
            )}
          </TabsContent>
        )}

        {/* Hours fills what is left of the window, like a list page: the
            totals stay put and the days scroll inside the table's own card. */}
        <TabsContent
          value="hours"
          className="mt-0 hidden min-h-0 flex-1 flex-col data-[state=active]:flex"
        >
          {problem ? (
            <PeriodUnset />
          ) : (
            <Timesheet
              from={from}
              to={to}
              employeeId={employeeId}
              typeById={typeById}
              canChoose={canChoose}
              canApprove={canApprove}
              isSelf={isSelf}
            />
          )}
        </TabsContent>

        {/* Not tied to the period: a correction asked for last week is still
            waiting whichever month the picker is on, and a queue that emptied
            itself when somebody changed the month would be missed. */}
        <TabsContent
          value="corrections"
          className="mt-0 hidden min-h-0 flex-1 flex-col data-[state=active]:flex"
        >
          <TimeCorrections employeeId={employeeId} canApprove={canApprove} canChoose={canChoose} />
        </TabsContent>
      </Tabs>

      {settling && (
        <SettleUnansweredDialog
          from={settling.from}
          to={settling.to}
          label={settling.label}
          employeeId={settling.employeeId}
          onClose={() => setSettling(null)}
        />
      )}

      {editing && (
        <DayDialog
          row={editing.row}
          day={editing.day}
          types={types.data ?? []}
          canApprove={canApprove}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void qc.invalidateQueries({ queryKey: ["attendance"] });
          }}
        />
      )}
    </div>
  );
}

/** Shown in place of a view while the typed range cannot be read. */
function PeriodUnset() {
  return (
    <p className="text-muted-foreground text-sm">Set a period above and the days appear here.</p>
  );
}

/**
 * A date range, picked off the calendar rather than typed into two boxes.
 *
 * Two native date inputs were what this was, and they asked somebody to know
 * the answer before they could give it: picking "the fortnight before the pay
 * run" or "from the Monday after the holiday" out of a pair of boxes means
 * working out two dates in your head first. A calendar is the question and the
 * answer in one, as long as it shows enough of the year to find them in.
 *
 * So it shows the months two abreast and scrolls, rather than paging one month
 * at a time behind arrows. A range nearly always straddles a month boundary —
 * the end of one and the start of the next — and with two visible both ends of
 * it can be clicked without the first click's month leaving the screen. Beyond
 * the pair, scrolling is how you reach last March: six presses of an arrow to
 * get there is why nobody used the arrows.
 *
 * Every month in the window is rendered and the box scrolls over them, which is
 * a fixed cost of about fifteen months of cells and no navigation state at all —
 * there is no "which month is showing", so nothing can disagree about it.
 */

/** How far back the scroll reaches: a year to date, and a closed year to correct. */
const RANGE_MONTHS_BACK = 13;
/** And forward, for a period somebody opens early. */
const RANGE_MONTHS_AHEAD = 2;

/** `YYYY-MM-DD` as a local `Date`, which is the only kind the calendar draws. */
function localDate(iso: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return undefined;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/**
 * Back to the stored form, read off the same local fields it was built from.
 *
 * Deliberately not `toISOString()`, which would shift the date by a day for
 * anybody west of Greenwich — the dates here key a day, not an instant, and a
 * round trip through UTC is how the 1st becomes the 31st.
 */
function isoOf(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `6 Oct 2026`, for the trigger. The boxes it replaces showed `2026-10-06`. */
function dayLabel(iso: string): string {
  const date = localDate(iso);
  if (!date) return iso;
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]?.slice(0, 3)} ${date.getFullYear()}`;
}

function RangeCalendarField({
  from,
  to,
  defaultOpen = false,
  onChange,
}: {
  from: string;
  to: string;
  /** Open the calendar as soon as the field appears. */
  defaultOpen?: boolean;
  /** Called once, with both ends, when a second date completes the range. */
  onChange: (from: string, to: string) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const complete = !!from && !!to && to >= from;

  return (
    <div className="space-y-2">
      {/*
       * The label is for screen readers only, as the month controls' is. The
       * dropdown immediately to the left already says "Dates" — printing it again
       * beside the button read as the word twice in a row, and a `Label` is
       * inline, so it sat *next to* the trigger instead of above it and squeezed
       * the dates into an ellipsis.
       */}
      <Label htmlFor="attendance-range" className="sr-only">
        Date range
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="attendance-range"
            variant="outline"
            className="w-[19rem] justify-start font-normal"
            aria-label="Pick a date range"
          >
            <CalendarRange className="mr-2 h-4 w-4 shrink-0" />
            {complete ? (
              <span className="truncate">
                {dayLabel(from)} – {dayLabel(to)}
                <span className="text-muted-foreground"> · {spanDays(from, to)} days</span>
              </span>
            ) : (
              <span className="text-muted-foreground">Pick a start and an end</span>
            )}
          </Button>
        </PopoverTrigger>
        {/* Mounted only while open, so the scroll lands on the chosen month
            every time it is opened rather than only the first time. */}
        <PopoverContent align="start" className="w-auto p-0">
          <MonthScroller
            from={from}
            to={to}
            onPicked={(nextFrom, nextTo) => {
              onChange(nextFrom, nextTo);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * The scrolling pair of months.
 *
 * The range is held here and handed up only once both ends are set. A
 * half-made range is not a period — committing it would fire a read of the one
 * day between the first click and the second, and draw the whole screen for it.
 */
function MonthScroller({
  from,
  to,
  onPicked,
}: {
  from: string;
  to: string;
  onPicked: (from: string, to: string) => void;
}) {
  const [draft, setDraft] = useState<DateRange | undefined>(() => {
    const start = localDate(from);
    return start ? { from: start, to: localDate(to) } : undefined;
  });

  /*
   * The window of months, wide enough to hold the range already chosen. A
   * period opened last January has to be visible when somebody opens the
   * picker to adjust it, which a window measured only from today would not be.
   */
  const { first, last, months, startIndex } = useMemo(() => {
    const today = new Date();
    const anchor = localDate(from) ?? today;
    const end = localDate(to) ?? anchor;

    const monthFloor = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
    const shifted = (date: Date, by: number) =>
      new Date(date.getFullYear(), date.getMonth() + by, 1);

    const lowest = monthFloor(
      shifted(today, -RANGE_MONTHS_BACK) < anchor ? shifted(today, -RANGE_MONTHS_BACK) : anchor,
    );
    const highest = monthFloor(
      shifted(today, RANGE_MONTHS_AHEAD) > end ? shifted(today, RANGE_MONTHS_AHEAD) : end,
    );

    const span =
      (highest.getFullYear() - lowest.getFullYear()) * 12 +
      (highest.getMonth() - lowest.getMonth()) +
      1;
    const offset =
      (monthFloor(anchor).getFullYear() - lowest.getFullYear()) * 12 +
      (monthFloor(anchor).getMonth() - lowest.getMonth());

    return { first: lowest, last: highest, months: span, startIndex: offset };
  }, [from, to]);

  const box = useRef<HTMLDivElement>(null);

  /*
   * Opened on the month the period starts in, not on the top of the window.
   * Before the paint, so it never shows last January on the way to this one.
   */
  useLayoutEffect(() => {
    const container = box.current;
    const grid = container?.querySelector<HTMLElement>(".rdp-month")?.parentElement;
    const target = grid?.children[startIndex] as HTMLElement | undefined;
    if (container && target) container.scrollTop = Math.max(0, target.offsetTop - 8);
  }, [startIndex]);

  return (
    <div>
      {/*
       * One row of two months is what the box is tall enough for, and the next
       * row shows through the bottom of it — which is the only thing that says
       * there is more calendar below without a scrollbar having to.
       */}
      <div
        ref={box}
        className="relative max-h-[22rem] snap-y snap-mandatory overflow-y-auto overscroll-contain"
      >
        <Calendar
          mode="range"
          /* Clicking a third time starts a new range rather than stretching
             the last one, which is what everybody expects of two clicks. */
          resetOnSelect
          /* The same ceiling the handler enforces, applied at the point of
             clicking: a range it would refuse can never be built here. */
          max={ATTENDANCE_MAX_RANGE_DAYS - 1}
          numberOfMonths={months}
          month={first}
          /* Both ends given, so nothing clamps the span: left open, the picker
             would quietly stop rendering months past the ones it assumed. */
          startMonth={first}
          endMonth={last}
          hideNavigation
          showOutsideDays={false}
          weekStartsOn={0}
          selected={draft}
          onSelect={(next) => {
            setDraft(next);
            if (next?.from && next.to) onPicked(isoOf(next.from), isoOf(next.to));
          }}
          /*
           * Two abreast, and the scroll snaps to a row of them.
           *
           * Without the snap it stops wherever the wheel left it, which is
           * usually halfway through a pair — and a month scrolled past its own
           * caption is a grid of numbers with no month name and no weekday row
           * above it, which is unreadable in exactly the way a calendar must
           * not be. Snapping means every resting position is two whole months.
           *
           * `rdp-month` is restated rather than dropped, because the scroll
           * above finds the row to open on by it.
           */
          classNames={{
            months: "grid grid-cols-2 gap-x-5 gap-y-5",
            month: "rdp-month flex w-full snap-start flex-col gap-4 scroll-mt-2",
          }}
        />
      </div>

      <p className="text-muted-foreground border-t px-3 py-2 text-xs">
        {draft?.from && !draft.to
          ? `From ${dayLabel(isoOf(draft.from))} — now pick the end.`
          : "Scroll for more months. Click a start, then an end."}
      </p>
    </div>
  );
}

/**
 * The period every view reads, and the person two of them do.
 *
 * Two ways of saying it, because people ask for both. A month is what somebody
 * wants nine times out of ten — and the arrows are for stepping through them in
 * order while the selects are for the month they actually want, which is often
 * last December. A date range is for the stretch that does not line up with a
 * month: a notice period, the fortnight before a pay run, a year to date.
 */
function PeriodControls({
  mode,
  onModeChange,
  year,
  month,
  onYear,
  onMonth,
  onStep,
  onToday,
  rangeFrom,
  rangeTo,
  onRangeFrom,
  onRangeTo,
  problem,
  person,
}: {
  mode: PeriodMode;
  onModeChange: (mode: PeriodMode) => void;
  year: number;
  month: number;
  onYear: (year: number) => void;
  onMonth: (month: number) => void;
  onStep: (by: number) => void;
  /** Back to the month that holds today. */
  onToday: () => void;
  rangeFrom: string;
  rangeTo: string;
  onRangeFrom: (date: string) => void;
  onRangeTo: (date: string) => void;
  problem: string | null;
  person: ReactNode;
}) {
  /*
   * Enough years either side to cover a correction to a closed year and a
   * period opened early, which is the whole span anybody navigates to.
   */
  const thisYear = new Date().getUTCFullYear();
  const years = Array.from({ length: 8 }, (_, i) => thisYear - 6 + i);

  const now = new Date();
  const onThisMonth = year === now.getUTCFullYear() && month === now.getUTCMonth();

  /*
   * Set when somebody picks "Dates" from the dropdown, so the calendar opens at
   * once — that is the next thing they have to do. Not set when the controls
   * merely remount on Dates (coming back from Time edits), where a calendar
   * springing open would be in the way.
   */
  const [pickedDates, setPickedDates] = useState(false);

  // One row like every other page's filters: whose, then which period. The
  // picker names the person and the selects name the month, so neither needs
  // a label above it.
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {person}

        {/* One dropdown picks how the period is chosen; the controls beside it
            follow from that choice, so only one way of choosing is ever shown. */}
        <Select
          value={mode}
          onValueChange={(value) => {
            const next = value as PeriodMode;
            if (next === "range") setPickedDates(true);
            onModeChange(next);
          }}
        >
          <SelectTrigger className="w-32" aria-label="Choose the period by">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">
              <span className="flex items-center gap-2">
                <CalendarDays className="h-3.5 w-3.5" />
                Month
              </span>
            </SelectItem>
            <SelectItem value="range">
              <span className="flex items-center gap-2">
                <CalendarRange className="h-3.5 w-3.5" />
                Dates
              </span>
            </SelectItem>
          </SelectContent>
        </Select>

        {mode === "month" ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => onStep(-1)}
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div>
              <Select value={String(month)} onValueChange={(v) => onMonth(Number(v))}>
                <SelectTrigger className="w-36" id="attendance-month" aria-label="Month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((name, index) => (
                    <SelectItem key={name} value={String(index)}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Select value={String(year)} onValueChange={(v) => onYear(Number(v))}>
              <SelectTrigger className="w-24" aria-label="Year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" size="icon" onClick={() => onStep(1)} aria-label="Next month">
              <ChevronRight className="h-4 w-4" />
            </Button>

            {!onThisMonth && (
              <Button variant="ghost" onClick={onToday}>
                Today
              </Button>
            )}
          </div>
        ) : (
          <RangeCalendarField
            from={rangeFrom}
            to={rangeTo}
            defaultOpen={pickedDates}
            /* Both ends at once, because that is how they are picked: a range
               set one field at a time passes through "end before start" on its
               way to being valid, and reads the window in between. */
            onChange={(nextFrom, nextTo) => {
              onRangeFrom(nextFrom);
              onRangeTo(nextTo);
            }}
          />
        )}
      </div>

      {problem && <p className="text-destructive text-sm">{problem}</p>}
    </div>
  );
}

/**
 * One person's attendance, laid out as the months it covers.
 *
 * Read for one employee rather than filtered out of the team's month: a
 * calendar shows one person by definition, and asking for forty to draw one is
 * a page of payload nobody looks at.
 */
function PersonCalendar({
  from,
  to,
  employeeId,
  canChoose,
  settling,
  types,
  typeById,
  onPick,
}: {
  from: string;
  to: string;
  employeeId: string;
  canChoose: boolean;
  /** True until the caller's own record is known, so "nobody" is not yet news. */
  settling: boolean;
  types: LeaveType[];
  typeById: Map<string, LeaveType>;
  onPick: (row: AttendanceGridRow, day: AttendanceGridDay) => void;
}) {
  const asOf = todayIso();

  const month = useQuery({
    queryKey: ["attendance", "person", employeeId, from, to],
    enabled: !!employeeId,
    queryFn: async () => {
      const result = await api.attendance.grid(from, to, employeeId);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });

  const row = month.data?.employees[0];
  const byDate = useMemo(
    () => new Map((row?.days ?? []).map((day) => [day.date, day])),
    [row?.days],
  );

  if (!employeeId) {
    if (settling) return <p className="text-muted-foreground text-sm">Loading…</p>;

    return (
      <Alert>
        <AlertTitle>{canChoose ? "Nobody chosen yet" : "No attendance to show"}</AlertTitle>
        <AlertDescription>
          {canChoose
            ? "Pick somebody above to see their month."
            : "Attendance belongs to an employee record, and this login is not linked to one. " +
              "Whoever runs the payroll can link it under People → Employees."}
        </AlertDescription>
      </Alert>
    );
  }

  if (month.isLoading) return <p className="text-muted-foreground text-sm">Loading…</p>;

  if (month.error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{(month.error as Error).message}</AlertDescription>
      </Alert>
    );
  }

  if (!row) return <p className="text-muted-foreground text-sm">No days in this period.</p>;

  /** Every calendar month the period touches, which is what the view draws. */
  const months = monthsBetween(from, to);

  return (
    <div className="space-y-4">
      {/* The period's totals first, as on every other page with a summary; the
          person is already named in the picker above, and the period in the
          month controls, so neither is repeated here. */}
      <CalendarSummary row={row} typeById={typeById} asOf={asOf} />

      <TooltipProvider delayDuration={200}>
        {months.length > 1 ? (
          /*
           * A period of several months is two months abreast in a box that
           * scrolls, rather than a column of them running off the bottom of the
           * page. A range is free to be a quarter or a year, and left to grow a
           * year drew twelve months the page had to be scrolled past — the
           * summary ended up somewhere below the fold, and comparing March with
           * September meant scrolling between them with nothing to compare
           * against on the way.
           *
           * Capped, two sit side by side and the next pair is one scroll away,
           * with the summary and the legend pinned below the box where they
           * stay in view for whichever months are showing.
           */
          <div className="space-y-6">
            <div className="max-h-[70vh] overflow-y-auto rounded-md border p-4">
              <div className="grid gap-6 lg:grid-cols-2">
                {months.map(({ year, month: index }) => (
                  <MonthCalendar
                    key={`${year}-${index}`}
                    year={year}
                    month={index}
                    from={from}
                    to={to}
                    asOf={asOf}
                    row={row}
                    byDate={byDate}
                    typeById={typeById}
                    onPick={onPick}
                  />
                ))}
              </div>
            </div>

            <PeriodPanel row={row} types={types} asOf={asOf} />
          </div>
        ) : (
          /*
           * One month, and the panel beside it rather than above and below. A
           * single month fills one of the two columns, so the summary and the
           * legend were two short bands spanning the full width while the space
           * beside the calendar stayed empty; as a cell of the same grid they
           * fill it. Nothing scrolls, because there is nothing to scroll past.
           */
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_18rem]">
            {months.map(({ year, month: index }) => (
              <MonthCalendar
                key={`${year}-${index}`}
                showCaption={false}
                year={year}
                month={index}
                from={from}
                to={to}
                asOf={asOf}
                row={row}
                byDate={byDate}
                typeById={typeById}
                onPick={onPick}
              />
            ))}

            <PeriodPanel row={row} types={types} asOf={asOf} />
          </div>
        )}
      </TooltipProvider>
    </div>
  );
}

/**
 * One calendar month.
 *
 * Weeks run Sunday to Saturday, as the worksheet's own columns do. Dates
 * outside the chosen period are left as empty squares rather than dropped: a
 * range that starts on the 14th should still read as a month with its first
 * fortnight missing, not as a month that begins on a Thursday.
 */
function MonthCalendar({
  showCaption = true,
  year,
  month,
  from,
  to,
  asOf,
  row,
  byDate,
  typeById,
  onPick,
}: {
  /** Off when the page's own month controls already name the one month shown. */
  showCaption?: boolean;
  year: number;
  month: number;
  from: string;
  to: string;
  asOf: string;
  row: AttendanceGridRow;
  byDate: Map<string, AttendanceGridDay>;
  typeById: Map<string, LeaveType>;
  onPick: (row: AttendanceGridRow, day: AttendanceGridDay) => void;
}) {
  const total = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const lead = new Date(Date.UTC(year, month, 1)).getUTCDay();

  const cells: (string | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from(
      { length: total },
      (_, i) => `${year}-${String(month + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`,
    ),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <section className="space-y-2">
      {showCaption && (
        <h3 className="font-medium">
          {MONTH_NAMES[month]} {year}
        </h3>
      )}

      <div className="overflow-hidden rounded-md border">
        <div className="grid grid-cols-7 bg-muted/50 text-center text-[11px] font-medium">
          {WEEKDAY_INITIALS.map((name) => (
            <div key={name} className="border-b p-1">
              {name}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {cells.map((date, index) => {
            const day = date && date >= from && date <= to ? byDate.get(date) : undefined;

            if (!day) {
              return (
                <div
                  key={date ?? `pad-${index}`}
                  className="bg-muted/20 min-h-20 border-r border-b [&:nth-child(7n)]:border-r-0"
                >
                  {date && (
                    <span className="text-muted-foreground block p-1.5 text-xs tabular-nums">
                      {Number(date.slice(8))}
                    </span>
                  )}
                </div>
              );
            }

            return (
              <DayTile
                key={date}
                row={row}
                day={day}
                asOf={asOf}
                typeById={typeById}
                onPick={onPick}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * One date, as its two halves.
 *
 * The halves are the point. A morning of leave and an afternoon at a client is
 * one day and two different answers, and drawing it as a single block would
 * either lose the leave or charge the whole day to it. Where both halves agree
 * — which is most days — they are one band, because `Office / Office` is a
 * worse way of writing `Office`.
 */
function DayTile({
  row,
  day,
  asOf,
  typeById,
  onPick,
}: {
  row: AttendanceGridRow;
  day: AttendanceGridDay;
  asOf: string;
  typeById: Map<string, LeaveType>;
  onPick: (row: AttendanceGridRow, day: AttendanceGridDay) => void;
}) {
  const am = effectiveStatus(day, "am", asOf);
  const pm = effectiveStatus(day, "pm", asOf);
  const amType = leaveTypeOf(day, "am");
  const pmType = leaveTypeOf(day, "pm");
  const same = am === pm && amType === pmType;
  const isToday = day.date === asOf;

  /*
   * A public holiday fills the tile rather than being written across it.
   *
   * It used to be the holiday's name in purple on an otherwise ordinary cell,
   * with a grey "not a working day" bar underneath — so a month's holidays read
   * as text to be found rather than as days to be seen, which is the one thing a
   * calendar is for. The whole cell takes the colour instead.
   *
   * `offDuty` is what decides whether the bars come back: a holiday somebody
   * actually worked still has to say so, and a sky-blue bar on purple is exactly
   * the thing worth noticing. A holiday nobody worked has nothing to add — the
   * bar would say "not a working day" on a cell that already says it in colour.
   */
  const offDuty = (status: TileStatus) => status === "non_working" || status === "holiday";
  const holidayFills = !!day.holiday_name && offDuty(am) && offDuty(pm);

  /*
   * The tooltip takes the cell's own colour, so the panel that opens over a day
   * is recognisably that day rather than the same teal box everywhere. Only
   * where the day has one colour to take: a day of two different halves has no
   * one colour, and the pale statuses — office, travel, training — are pale
   * because they are read against white, so a tooltip's text would vanish on
   * them. `FILLED_TOOLTIP_STATUSES` is the list of the ones that do carry it.
   *
   * A leave type comes with its own colour and so is set as a style; the rest
   * are Tailwind classes, which `cn` merges over the tooltip's default.
   */
  const amTypeRow = amType ? typeById.get(amType) : undefined;
  const leaveFill = same && am === "leave" && amTypeRow ? leaveTypeColour(amTypeRow) : null;
  const statusFill =
    same && FILLED_TOOLTIP_STATUSES.includes(am) ? STATUS_STYLE[am].tileClassName : null;

  const band = (half: DayHalf, status: TileStatus, typeId: string | null, label: boolean) => {
    const tone = halfTone(status, typeId, typeById, "tile");
    const text = halfShort(status, typeId, typeById);
    // A half with nothing to say — a day off, say — draws nothing, rather than
    // an empty grey bar on every weekend that reads as missing data.
    if (!text) return null;

    return (
      <div
        className={cn(
          "flex h-5 w-full min-w-0 items-center gap-1 overflow-hidden rounded px-1 text-[10px] font-medium",
          tone.className,
        )}
        style={tone.style}
      >
        {label && text && (
          <span className="shrink-0 opacity-70">{half === "am" ? "AM" : "PM"}</span>
        )}
        <span className="truncate">{text}</span>
      </div>
    );
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => onPick(row, day)}
          disabled={day.locked}
          // The label overrides the cell's own text, so the holiday's name has to
          // be said here or it is only in the colour — which is nothing to read.
          aria-label={`${day.date}${day.holiday_name ? ` — ${day.holiday_name}` : ""} — ${halfName(
            am,
            amType,
            typeById,
          )} morning, ${halfName(pm, pmType, typeById)} afternoon`}
          className={cn(
            "flex min-h-20 min-w-0 flex-col gap-1 border-r border-b p-1.5 text-left",
            "[&:nth-child(7n)]:border-r-0",
            // Days off are shaded, so the working weeks read as blocks.
            !holidayFills && am === "non_working" && pm === "non_working" && "bg-muted/40",
            isToday && "ring-primary ring-2 ring-inset",
            holidayFills && "bg-purple-600 text-white",
            day.locked
              ? "cursor-not-allowed opacity-70"
              : "cursor-pointer hover:ring-2 hover:ring-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
          )}
        >
          <div className="flex items-start justify-between gap-1">
            <span
              className={cn(
                "text-xs tabular-nums",
                isToday
                  ? "bg-primary text-primary-foreground rounded px-1.5 font-semibold"
                  : "font-medium",
              )}
            >
              {Number(day.date.slice(8))}
            </span>
            <span
              className={cn(
                "flex items-center gap-0.5",
                holidayFills ? "text-white/80" : "text-muted-foreground",
              )}
            >
              {day.note && <StickyNote className="h-3 w-3" />}
              {day.locked && <Lock className="h-3 w-3" />}
            </span>
          </div>

          {/* The holiday's own name, which is the thing a calendar can say and a
              letter in a grid cannot. */}
          {day.holiday_name && (
            <span
              className={cn(
                "line-clamp-2 text-[10px] leading-tight",
                holidayFills ? "font-medium text-white" : "text-purple-700 dark:text-purple-300",
              )}
            >
              {day.holiday_name}
            </span>
          )}

          {!holidayFills && (
            <div className="mt-auto w-full min-w-0 space-y-0.5">
              {same ? (
                band("am", am, amType, false)
              ) : (
                <>
                  {band("am", am, amType, true)}
                  {band("pm", pm, pmType, true)}
                </>
              )}
            </div>
          )}
        </button>
      </TooltipTrigger>

      <TooltipContent
        className={cn("max-w-64", statusFill, holidayFills && "bg-purple-600 text-white")}
        style={leaveFill ? { backgroundColor: leaveFill } : undefined}
      >
        <p className="font-medium">
          {WEEKDAY_INITIALS[new Date(`${day.date}T00:00:00Z`).getUTCDay()]} {day.date}
        </p>

        {/* Named as what it is, not only as what it is called. "H1" on its own
            is a label somebody has to already know; "Public holiday — H1" is
            the same label and the thing it means. */}
        {day.holiday_name && (
          <p className="mt-0.5">
            <span className="font-semibold">Public holiday</span> — {day.holiday_name}
          </p>
        )}

        {/* On a holiday nobody worked, the two halves would say "not a working
            day" twice under a line that has just said so once. */}
        {holidayFills ? (
          <p className="opacity-80">No work was due on this day.</p>
        ) : (
          <>
            <p>Morning: {halfName(am, amType, typeById)}</p>
            <p>Afternoon: {halfName(pm, pmType, typeById)}</p>
          </>
        )}
        {day.note && <p className="mt-1">{day.note}</p>}
        {day.locked && (
          <p className="mt-1 flex items-center gap-1 opacity-80">
            <Lock className="h-3 w-3" /> Locked by a closed pay period
          </p>
        )}
        {!day.locked && (day.evidence as { reason?: string } | null)?.reason && (
          <p className="mt-1 opacity-80">{(day.evidence as { reason: string }).reason}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The period's four figures, as a strip of cards above the calendar — the same
 * summary cards the invoices and the Virrgin account open with. The panel
 * beside the calendar still breaks each figure down; these are the totals a
 * reader wants before reading any day.
 */
function CalendarSummary({
  row,
  typeById,
  asOf,
}: {
  row: AttendanceGridRow;
  typeById: Map<string, LeaveType>;
  asOf: string;
}) {
  const tally = useMemo(() => tallyHalves(row.days, asOf), [row.days, asOf]);
  const sum = (values: (number | undefined)[]) => values.reduce<number>((t, v) => t + (v ?? 0), 0);

  const worked = sum(WORKING_ATTENDANCE_STATUSES.map((status) => tally.byStatus[status]));
  const leave = sum(Object.values(tally.byLeaveType));
  const absent = tally.byStatus.absent_unauthorised ?? 0;
  const unanswered = tally.byStatus.unconfirmed ?? 0;
  const unpaid = row.totals.unpaid_halves;
  const leaveKinds = Object.keys(tally.byLeaveType).filter((id) => tally.byLeaveType[id]);

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <StatCard label="Days worked" icon={Briefcase} value={String(halvesToDays(worked))} />
      <StatCard
        label="Time off"
        icon={Palmtree}
        value={String(halvesToDays(leave + absent))}
        hint={
          leaveKinds.length
            ? leaveKinds.map((id) => typeById.get(id)?.name ?? "Leave").join(", ")
            : absent
              ? undefined
              : "No leave taken"
        }
        tone={absent ? "danger" : "ok"}
      />
      <StatCard
        label="Not yet answered"
        icon={HelpCircle}
        value={String(halvesToDays(unanswered))}
        hint={unanswered ? "Click a day to answer it" : "Every day has an answer"}
        tone={unanswered ? "warn" : "ok"}
      />
      <StatCard
        label="Unpaid"
        icon={CircleDollarSign}
        value={String(halvesToDays(unpaid))}
        hint="Day(s) deducted from pay"
        tone={unpaid ? "danger" : "ok"}
      />
    </div>
  );
}

/**
 * The panel beside the calendar: what the period came to, and what every mark
 * on it means.
 *
 * These were two bands before — a summary above the calendar and a legend below
 * it — and between them they named each leave type twice: once as a coloured
 * dot with a count, once as its letter with a name. One line now carries both
 * readings, so "what is this mark" and "how many" are answered by the same row
 * and the colour on it is the colour the calendar draws.
 *
 * The marks with nothing to count are kept, under their own heading. A legend
 * exists for the marks somebody cannot name, and a dashed square for a day not
 * yet arrived is exactly that whether or not the period contains one.
 */
function PeriodPanel({
  row,
  types,
  asOf,
}: {
  row: AttendanceGridRow;
  types: LeaveType[];
  asOf: string;
}) {
  const tally = useMemo(() => tallyHalves(row.days, asOf), [row.days, asOf]);

  const worked = WORKING_ATTENDANCE_STATUSES.filter((status) => (tally.byStatus[status] ?? 0) > 0);
  const workedHalves = WORKING_ATTENDANCE_STATUSES.reduce(
    (total, status) => total + (tally.byStatus[status] ?? 0),
    0,
  );

  return (
    <aside className="h-fit space-y-5 rounded-md border p-4">
      <section className="space-y-2">
        <h3 className="text-xs font-semibold tracking-wide uppercase">
          Worked — {halvesToDays(workedHalves)} day(s)
        </h3>
        {worked.length === 0 ? (
          <p className="text-muted-foreground text-sm">Nothing recorded as worked yet.</p>
        ) : (
          <dl className="space-y-1 text-sm">
            {worked.map((status) => (
              <PanelLine
                key={status}
                code={STATUS_STYLE[status].code}
                label={STATUS_STYLE[status].label}
                swatch={STATUS_STYLE[status].tileClassName}
                halves={tally.byStatus[status] ?? 0}
              />
            ))}
          </dl>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold tracking-wide uppercase">Time off</h3>
        <dl className="space-y-1 text-sm">
          {types
            .filter((type) => type.active)
            .map((type) => (
              <PanelLine
                key={type.id}
                code={type.code}
                label={type.name}
                colour={leaveTypeColour(type)}
                halves={tally.byLeaveType[type.id] ?? 0}
              />
            ))}
          <PanelLine
            code={STATUS_STYLE.holiday.code}
            label={STATUS_STYLE.holiday.label}
            swatch={STATUS_STYLE.holiday.tileClassName}
            halves={tally.byStatus.holiday ?? 0}
          />
          <PanelLine
            code={STATUS_STYLE.absent_unauthorised.code}
            label={STATUS_STYLE.absent_unauthorised.label}
            swatch={STATUS_STYLE.absent_unauthorised.tileClassName}
            halves={tally.byStatus.absent_unauthorised ?? 0}
          />
        </dl>

        {/*
         * The one figure the lines above do not already carry: what the period
         * costs. Shown only when it is not zero, because a nil deduction is not
         * news. The unanswered halves had a badge here too and no longer do —
         * they are a line of their own below, and saying it twice was the
         * duplication this panel exists to remove.
         */}
        {row.totals.unpaid_halves > 0 && (
          <Badge
            variant="outline"
            className="border-red-400 text-red-700 dark:border-red-500/60 dark:text-red-300"
          >
            {halvesToDays(row.totals.unpaid_halves)} day(s) unpaid
          </Badge>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold tracking-wide uppercase">Also on the calendar</h3>
        <dl className="space-y-1 text-sm">
          <PanelLine
            code={STATUS_STYLE.unconfirmed.code}
            label={STATUS_STYLE.unconfirmed.label}
            swatch={STATUS_STYLE.unconfirmed.tileClassName}
            halves={tally.byStatus.unconfirmed ?? 0}
          />
          <PanelLine
            code={STATUS_STYLE.upcoming.code}
            label={STATUS_STYLE.upcoming.label}
            swatch={STATUS_STYLE.upcoming.tileClassName}
            halves={null}
          />
        </dl>
      </section>
    </aside>
  );
}

/**
 * One line of the panel: the mark, what it means, and how much of it there is.
 *
 * The mark is drawn the way the calendar draws it — a leave type's own colour
 * with its own letter on it — so the panel is a key rather than a second colour
 * scheme that has to be kept in step with the first. `halves` of null is for the
 * marks that are not a quantity: a day still to come is a state, not a total.
 */
function PanelLine({
  code,
  label,
  halves,
  colour,
  swatch,
}: {
  code: string;
  label: string;
  halves: number | null;
  colour?: string;
  swatch?: string;
}) {
  const none = halves === 0 || halves === null;

  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-semibold",
          swatch,
          colour && "text-white",
        )}
        style={colour ? { backgroundColor: colour } : undefined}
      >
        {code}
      </span>
      <dt className={cn("truncate", none && "text-muted-foreground")}>{label}</dt>
      {halves !== null && (
        <dd
          className={cn(
            "ml-auto shrink-0 tabular-nums",
            none ? "text-muted-foreground" : "font-medium",
          )}
        >
          {halvesToDays(halves)}
        </dd>
      )}
    </div>
  );
}

/**
 * The whole team against every date — the worksheet this replaces.
 *
 * One request for everybody, because that is what the sheet was and a request
 * per employee would be forty round trips to draw one page.
 */
/*
 * The totals at the right of the team grid stay put while the dates scroll
 * between them and the names, so a row's figures are always beside it. Each
 * column is 5rem wide, which is what the offsets below count in.
 */
const TOTAL_COLUMNS = [
  { heading: "Worked", pin: "right-40" },
  { heading: "Absent", pin: "right-20" },
  { heading: "Unpaid", pin: "right-0" },
] as const;
const TOTAL_CELL =
  "bg-card group-hover:bg-muted sticky z-10 w-20 min-w-20 border-b px-2 text-center tabular-nums group-last:border-b-0";

function TeamGrid({
  from,
  to,
  types,
  typeById,
  onPick,
}: {
  from: string;
  to: string;
  types: LeaveType[];
  typeById: Map<string, LeaveType>;
  onPick: (row: AttendanceGridRow, day: AttendanceGridDay) => void;
}) {
  const grid = useQuery({
    queryKey: ["attendance", "team", from, to],
    queryFn: async () => {
      const result = await api.attendance.grid(from, to);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });

  const asOf = todayIso();

  const dates = grid.data?.employees[0]?.days.map((d) => d.date) ?? [];
  /*
   * Counted here rather than taken from `row.totals.unconfirmed_halves`, for
   * the same reason `PeriodPanel` counts its own: the server's total is every
   * empty working day in the window, and in a window that runs past today most
   * of those are days nobody can answer yet. A badge saying "22 days
   * unanswered" on the first of the month is a badge nobody can clear.
   */
  const unanswered = (grid.data?.employees ?? []).reduce(
    (total, row) => total + (tallyHalves(row.days, asOf).byStatus.unconfirmed ?? 0),
    0,
  );

  if (grid.isLoading) return <p className="text-muted-foreground text-sm">Loading…</p>;

  if (grid.error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{(grid.error as Error).message}</AlertDescription>
      </Alert>
    );
  }

  if ((grid.data?.employees.length ?? 0) === 0) {
    return (
      <Alert>
        <AlertTitle>Nobody is on the payroll yet</AlertTitle>
        <AlertDescription>
          Add employees under <strong>People → Employees</strong> and their grid appears here.
        </AlertDescription>
      </Alert>
    );
  }

  const employees = grid.data?.employees ?? [];
  const isWeekend = (date: string) => {
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    return weekday === 0 || weekday === 6;
  };

  // A plain table, with only today's column and the weekends shaded so the
  // weeks read at a glance.
  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-3">
        {unanswered > 0 && (
          <p className="text-warning-foreground dark:text-warning flex items-center gap-1.5 text-xs">
            <HelpCircle className="h-3.5 w-3.5" />
            {halvesToDays(unanswered)} day(s) not yet answered
          </p>
        )}
        <div className="overflow-hidden rounded-md border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0 text-xs">
              <thead>
                <tr>
                  <th className="bg-card sticky left-0 z-20 min-w-56 border-b px-3 py-2 text-left font-semibold shadow-[inset_-1px_0_0_var(--color-border)]">
                    Employee
                  </th>
                  {dates.map((date) => {
                    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
                    const today = date === asOf;
                    return (
                      <th
                        key={date}
                        className={cn(
                          "min-w-9 border-b px-0.5 py-2 text-center font-medium",
                          isWeekend(date) && "bg-muted/50",
                          today && "bg-primary/10",
                        )}
                      >
                        <div
                          className={cn(
                            "text-[10px] font-normal",
                            today ? "text-primary font-semibold" : "text-muted-foreground",
                          )}
                        >
                          {WEEKDAY_INITIALS[weekday]}
                        </div>
                        <div
                          className={cn(
                            "mx-auto mt-0.5 flex h-6 w-6 items-center justify-center rounded-full tabular-nums",
                            today && "bg-primary text-primary-foreground font-semibold",
                            !today && isWeekend(date) && "text-muted-foreground",
                          )}
                        >
                          {Number(date.slice(8))}
                        </div>
                      </th>
                    );
                  })}
                  {TOTAL_COLUMNS.map((column, index) => (
                    <th
                      key={column.heading}
                      className={cn(
                        "bg-card sticky z-20 w-20 min-w-20 border-b px-2 py-2 text-center font-semibold",
                        column.pin,
                        index === 0 && "shadow-[inset_1px_0_0_var(--color-border)]",
                      )}
                    >
                      {column.heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map((row) => (
                  <tr key={row.employee.id} className="group">
                    <td className="bg-card group-hover:bg-muted sticky left-0 z-10 max-w-56 border-b px-3 py-2 shadow-[inset_-1px_0_0_var(--color-border)] group-last:border-b-0">
                      <Truncated text={row.employee.full_name} className="font-medium" />
                      {row.employee.job_title && (
                        <Truncated
                          text={row.employee.job_title}
                          className="text-muted-foreground text-[10px]"
                        />
                      )}
                    </td>

                    {row.days.map((day) => (
                      <DayCell
                        key={day.date}
                        row={row}
                        day={day}
                        typeById={typeById}
                        asOf={asOf}
                        weekend={isWeekend(day.date)}
                        onClick={() => onPick(row, day)}
                      />
                    ))}

                    <td
                      className={cn(
                        TOTAL_CELL,
                        TOTAL_COLUMNS[0]!.pin,
                        "shadow-[inset_1px_0_0_var(--color-border)]",
                      )}
                    >
                      {halvesToDays(row.totals.worked_halves)}
                    </td>
                    <td className={cn(TOTAL_CELL, TOTAL_COLUMNS[1]!.pin)}>
                      {halvesToDays(row.totals.absence_halves)}
                    </td>
                    <td className={cn(TOTAL_CELL, TOTAL_COLUMNS[2]!.pin)}>
                      <span
                        className={cn(
                          "tabular-nums",
                          row.totals.unpaid_halves > 0 && "font-semibold text-destructive",
                        )}
                      >
                        {halvesToDays(row.totals.unpaid_halves)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <Legend types={types} />
      </div>
    </TooltipProvider>
  );
}

/**
 * One day, drawn as its two halves.
 *
 * A day whose morning and afternoon agree is a single block; one that differs is
 * split down the middle, which is how `L1` and `L2` read on the worksheet.
 */
function DayCell({
  row,
  day,
  typeById,
  asOf,
  weekend,
  onClick,
}: {
  row: AttendanceGridRow;
  day: AttendanceGridDay;
  typeById: Map<string, LeaveType>;
  asOf: string;
  weekend: boolean;
  onClick: () => void;
}) {
  /*
   * Through `effectiveStatus`, so a working day still to come draws blank here
   * exactly as it does on the calendar. The grid read the stored status direct
   * and so printed `?` — "not yet answered" — against every remaining date of
   * the month, which is most of the grid for most of the month.
   */
  const status = (half: DayHalf) => effectiveStatus(day, half, asOf);
  const label = (half: DayHalf) => halfCode(status(half), leaveTypeOf(day, half), typeById);
  const describe = (half: DayHalf) => halfName(status(half), leaveTypeOf(day, half), typeById);

  const same = status("am") === status("pm") && day.am_leave_type_id === day.pm_leave_type_id;
  const today = day.date === asOf;

  /*
   * One half as a chip. The blank statuses — a day off, a working day still to
   * come, a normal day in the office — draw nothing, as the grid always has.
   */
  const chip = (half: DayHalf, side: "both" | "left" | "right") => {
    const s = status(half);
    if (s === "non_working" || s === "upcoming" || s === "office") {
      return <span className="flex-1" />;
    }
    const tone = halfTone(s, leaveTypeOf(day, half), typeById, "grid");
    return (
      <span
        className={cn(
          "flex flex-1 items-center justify-center",
          side === "both" ? "rounded-md" : side === "left" ? "rounded-l-md" : "rounded-r-md",
          tone.className,
        )}
        style={tone.style}
      >
        {label(half)}
      </span>
    );
  };

  return (
    <td
      className={cn(
        "group-hover:bg-muted/40 border-b p-0.5 group-last:border-b-0",
        weekend && "bg-muted/50",
        today && "bg-primary/10",
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            disabled={day.locked}
            className={cn(
              "flex h-8 w-full items-stretch gap-px rounded-md text-[10px] font-semibold transition-shadow",
              day.locked
                ? "cursor-not-allowed opacity-70"
                : "cursor-pointer hover:ring-2 hover:ring-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
              // A proposal the platform made, that nobody has confirmed, is drawn
              // lighter than a day somebody settled.
              day.proposed && "opacity-70",
            )}
            aria-label={`${row.employee.full_name}, ${day.date}: ${describe("am")} morning, ${describe("pm")} afternoon`}
          >
            {same ? (
              chip("am", "both")
            ) : (
              <>
                {chip("am", "left")}
                {chip("pm", "right")}
              </>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-64">
          <p className="font-medium">
            {day.date} — {row.employee.full_name}
          </p>
          <p>Morning: {describe("am")}</p>
          <p>Afternoon: {describe("pm")}</p>
          {day.locked && (
            <p className="mt-1 flex items-center gap-1 text-muted-foreground">
              <Lock className="h-3 w-3" /> Locked by a closed pay period
            </p>
          )}
          {(day.evidence as { reason?: string } | null)?.reason && (
            <p className="mt-1 text-muted-foreground">
              {(day.evidence as { reason: string }).reason}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </td>
  );
}

/**
 * The legend under the team grid, built from the configured leave types rather
 * than hardcoded.
 *
 * The grid needs all of it — every cell is a letter, and a letter is a legend
 * entry or a puzzle. The single-person calendar used to show a second copy of
 * this below its months; it now names each mark once, beside the count of it,
 * in `PeriodPanel`.
 */
function Legend({ types }: { types: LeaveType[] }) {
  const statuses: TileStatus[] = [
    "wfh",
    "client_site",
    "holiday",
    "absent_unauthorised",
    "unconfirmed",
  ];
  const swatch =
    "flex h-5 min-w-5 items-center justify-center rounded px-1 text-[10px] font-semibold";

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
      {statuses.map((status) => (
        <span key={status} className="flex items-center gap-1.5">
          <span className={cn(swatch, STATUS_STYLE[status].className)}>
            {STATUS_STYLE[status].code}
          </span>
          {STATUS_STYLE[status].label}
        </span>
      ))}

      {types
        .filter((t) => t.active)
        .map((type) => (
          <span key={type.id} className="flex items-center gap-1.5">
            <span
              className={cn(swatch, "text-white")}
              style={{ backgroundColor: leaveTypeColour(type) }}
            >
              {type.code}
            </span>
            {type.name}
          </span>
        ))}
    </div>
  );
}

/**
 * Settling one day.
 *
 * The statuses an employee may set for themselves are the ones that cost
 * nothing: where they worked from. Leave is not among them — it goes through a
 * request so the allowance is checked — and neither is an unauthorised absence,
 * which only a manager can record. Both restrictions are enforced on the server;
 * the form simply does not offer what would be refused.
 */
function DayDialog({
  row,
  day,
  types,
  canApprove,
  onClose,
  onSaved,
}: {
  row: AttendanceGridRow;
  day: AttendanceGridDay;
  types: LeaveType[];
  canApprove: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [amStatus, setAmStatus] = useState<AttendanceStatus>(day.am_status);
  const [pmStatus, setPmStatus] = useState<AttendanceStatus>(day.pm_status);
  const [amType, setAmType] = useState<string | null>(day.am_leave_type_id);
  const [pmType, setPmType] = useState<string | null>(day.pm_leave_type_id);
  const [note, setNote] = useState(day.note ?? "");

  const selectable = ATTENDANCE_STATUSES.filter((status) => {
    if (status === "unconfirmed") return false;
    if (status === "non_working" || status === "holiday") return canApprove;
    if (status === "absent_unauthorised") return canApprove;
    if (status === "leave") return canApprove;
    return true;
  });

  const save = useMutation({
    mutationFn: async () => {
      const result = await api.attendance.setDay(row.employee.id, day.date, {
        am_status: amStatus,
        pm_status: pmStatus,
        am_leave_type_id: amStatus === "leave" ? amType : null,
        pm_leave_type_id: pmStatus === "leave" ? pmType : null,
        note: note.trim() || null,
      });
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      notify.success(`${day.date} recorded`);
      onSaved();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const half = (
    label: string,
    status: AttendanceStatus,
    setStatus: (s: AttendanceStatus) => void,
    typeId: string | null,
    setTypeId: (t: string | null) => void,
  ) => (
    <div className="space-y-2">
      <Label required={status === "leave"}>{label}</Label>
      <Select value={status} onValueChange={(v) => setStatus(v as AttendanceStatus)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {selectable.map((option) => (
            <SelectItem key={option} value={option}>
              {STATUS_STYLE[option].label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {status === "leave" && (
        <Select value={typeId ?? ""} onValueChange={(v) => setTypeId(v)}>
          <SelectTrigger>
            <SelectValue placeholder="Which kind of leave" />
          </SelectTrigger>
          <SelectContent>
            {types
              .filter((t) => t.active)
              .map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.code} — {type.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {row.employee.full_name} — {day.date}
          </DialogTitle>
          <DialogDescription>
            {day.proposed
              ? "Nothing is recorded for this day yet; this is what the logins suggest."
              : (day.evidence as { reason?: string } | null)?.reason ||
                "Recorded on the attendance grid."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          {half("Morning", amStatus, setAmStatus, amType, setAmType)}
          {half("Afternoon", pmStatus, setPmStatus, pmType, setPmType)}
        </div>

        {!canApprove && (
          <p className="text-muted-foreground text-xs">
            To record time off, raise a leave request — that is what checks your balance.
          </p>
        )}

        <div className="space-y-2">
          <Label htmlFor="attendance-note">Note</Label>
          <Input
            id="attendance-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional — what happened"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={
              save.isPending ||
              (amStatus === "leave" && !amType) ||
              (pmStatus === "leave" && !pmType)
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** `142` minutes as `2h 22m`. */
function duration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return hours > 0 ? `${hours}h ${rest}m` : `${rest}m`;
}

/**
 * Clocking in and out.
 *
 * The verdict is shown rather than hidden behind a success toast, because the
 * interesting outcome is the one where the clock-in worked and *did not count*
 * — a Saturday somebody caught up on, which is not a working day until a
 * manager says it was. Telling them at the moment they press the button is the
 * difference between a question they can resolve today and a figure they
 * discover in a payslip.
 */
/**
 * This person's own clock.
 *
 * One query behind both the card and the timesheet, which share the key rather
 * than the call: the timesheet needs it only to know whose hours to open on,
 * and a second request for that would be a second request for the same answer.
 */
function useClock() {
  return useQuery({
    queryKey: ["clock"],
    queryFn: async () => {
      const result = await api.attendance.clock();
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    // Keeps the hours-so-far honest while somebody has the page open.
    refetchInterval: 60_000,
  });
}

/**
 * The caller's clock, as it sits in the page header: where they stand today,
 * and the one button that changes it. The day's entries are a hover away on
 * the status, since they are for checking rather than for reading every time.
 */
function ClockControl() {
  const qc = useQueryClient();
  const clock = useClock();

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["clock"] });
    void qc.invalidateQueries({ queryKey: ["attendance"] });
  };

  const clockIn = useMutation({
    mutationFn: async () => {
      const result = await api.attendance.clockIn();
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (data) => {
      if (data?.counted) notify.success(data.message);
      else notify.error(data?.message ?? "Clocked in, but it does not count yet.");
      refresh();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const clockOut = useMutation({
    mutationFn: async () => {
      const result = await api.attendance.clockOut();
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (data) => {
      notify.success(data?.message ?? "Clocked out");
      refresh();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  // Not everybody with a login is on the payroll, and that is not an error.
  if (!clock.data) return null;

  const { clocked_in: clockedIn, open_entry: openEntry, worked, entries } = clock.data;

  const part =
    worked.am && worked.pm
      ? "morning and afternoon"
      : worked.am
        ? "morning"
        : worked.pm
          ? "afternoon"
          : "not yet a half day";

  const status = (
    <div className="flex h-9 items-center gap-2 rounded-md border bg-card px-3 text-sm">
      <span
        className={cn(
          "h-2 w-2 shrink-0 rounded-full",
          clockedIn ? "bg-emerald-500" : "bg-muted-foreground/40",
        )}
        aria-hidden
      />
      <span className="font-medium">
        {clockedIn
          ? `Clocked in${openEntry ? ` since ${timeOfDay(openEntry.started_at)}` : ""}`
          : "Not clocked in"}
      </span>
      <span className="text-muted-foreground tabular-nums">
        · {duration(worked.total_minutes)} today
      </span>
    </div>
  );

  return (
    <div className="flex items-center gap-2">
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{status}</TooltipTrigger>
          <TooltipContent className="max-w-64">
            <p className="font-medium">Today — {part}</p>
            {entries.length === 0 ? (
              <p className="opacity-80">Nothing clocked yet.</p>
            ) : (
              <ul className="mt-1 space-y-0.5">
                {entries.map((entry) => (
                  <li key={entry.started_at} className="tabular-nums">
                    {timeOfDay(entry.started_at)} –{" "}
                    {entry.ended_at ? timeOfDay(entry.ended_at) : "now"}
                    {entry.status === "pending" && " · not counting"}
                    {entry.auto_closed && " · closed automatically"}
                  </li>
                ))}
              </ul>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {clockedIn ? (
        <Button onClick={() => clockOut.mutate()} disabled={clockOut.isPending} variant="outline">
          <Clock className="mr-2 h-4 w-4" />
          Clock out
        </Button>
      ) : (
        <Button onClick={() => clockIn.mutate()} disabled={clockIn.isPending}>
          <Clock className="mr-2 h-4 w-4" />
          Clock in
        </Button>
      )}
    </div>
  );
}

/*
 * The headings and the total, pinned to the ends of the timesheet's scroll box.
 *
 * The tint is opaque rather than `bg-muted/50`: a translucent heading lets the
 * day scrolling beneath it show through, which is worse than no heading at all.
 * The rule against the days is an inset shadow for the reason given where these
 * are used — the cell's own border belongs to the collapsed grid and scrolls
 * away with it.
 */
const STUCK_TOP =
  "bg-card text-muted-foreground sticky top-0 z-20 h-10 px-3 font-medium shadow-[inset_0_-1px_0_var(--color-border)]";
const STUCK_BOTTOM =
  "bg-muted sticky bottom-0 z-20 px-3 py-2 shadow-[inset_0_1px_0_var(--color-border)]";

/**
 * The timesheet: the same month as the grid, in hours.
 *
 * The grid answers "was this person working". This answers "for how long", and
 * they are different questions — somebody can be marked present for a morning
 * they spent half of. Every date in the month has a row whether anything was
 * clocked on it or not, because the day somebody forgot to clock in is exactly
 * the row that needs to be visible.
 *
 * Counted and uncounted hours are shown apart, never summed. Time clocked
 * outside somebody's working pattern is recorded in full and counts for nothing
 * until a manager says what it was: one total would hide precisely the hours
 * worth asking about, which is the opposite of what a timesheet is for.
 */
function Timesheet({
  from,
  to,
  employeeId,
  typeById,
  canChoose,
  canApprove,
  isSelf,
}: {
  from: string;
  to: string;
  /** Whose hours — the page's own choice of person, shared with the calendar. */
  employeeId: string;
  /** So a day of leave is named and coloured here as it is on the calendar. */
  typeById: Map<string, LeaveType>;
  canChoose: boolean;
  canApprove: boolean;
  /** Whether these are the caller's own hours, which is what the wording turns on. */
  isSelf: boolean;
}) {
  const qc = useQueryClient();
  const asOf = todayIso();
  const [settling, setSettling] = useState<{ employeeId: string; entry: TimesheetEntry } | null>(
    null,
  );
  const [amending, setAmending] = useState<TimesheetDay | null>(null);

  const sheet = useQuery({
    queryKey: ["timesheet", from, to, employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const result = await api.attendance.timesheet(from, to, employeeId);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });

  const row: TimesheetRow | undefined = sheet.data?.employees[0];

  /*
   * This person's corrections, so a day already waiting on an admin says so
   * rather than inviting a second request for the same thing — and so the
   * allowance line at the top is the same figure the dialog will apply.
   */
  const corrections = useQuery({
    queryKey: ["time_amendments", employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const result = await api.timeAmendments.list({ employeeId });
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
  });

  const allowance = useQuery({
    queryKey: ["amendment_allowance", employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const result = await api.timeAmendments.allowance(employeeId);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });

  const pending = useMemo(() => pendingByEntry(corrections.data ?? []), [corrections.data]);

  if (!employeeId) {
    return (
      <Alert>
        <AlertTitle>{canChoose ? "Nobody chosen yet" : "Nobody to show hours for"}</AlertTitle>
        <AlertDescription>
          {canChoose
            ? "Pick somebody above to see their hours."
            : "Hours come from clocking in and out, which needs a login linked to an employee record."}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {sheet.isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : sheet.error ? (
        <Alert variant="destructive">
          <AlertDescription>{(sheet.error as Error).message}</AlertDescription>
        </Alert>
      ) : !row ? (
        <p className="text-muted-foreground text-sm">No hours for this month.</p>
      ) : (
        <>
          {/*
           * How many corrections are left, said where the timesheet is read
           * rather than only inside the dialog. Somebody scanning their month
           * for the days that are wrong should know what fixing them costs
           * before they start, and it is the only number on this screen that
           * changes what a button will do.
           */}
          {/* The period's hours as summary cards, like the calendar's days. The
              corrections left are the one figure that changes what a button
              will do, so they are said here, before anybody starts fixing. */}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Hours counted"
              icon={Timer}
              value={duration(row.totals.counted_minutes)}
              hint={`of ${duration(row.totals.contracted_minutes)} contracted`}
            />
            <StatCard
              label="Days clocked"
              icon={CalendarDays}
              value={String(row.totals.days_clocked)}
              hint={`of ${row.totals.working_days} working day(s)`}
            />
            <StatCard
              label="Not counting"
              icon={HelpCircle}
              value={duration(row.totals.uncounted_minutes)}
              hint={
                row.totals.days_pending
                  ? `On ${row.totals.days_pending} day(s) outside the working pattern`
                  : "Nothing waiting"
              }
              tone={row.totals.uncounted_minutes > 0 ? "danger" : "ok"}
            />
            {isSelf && allowance.data && (
              <StatCard
                label="Corrections left"
                icon={Pencil}
                value={`${allowance.data.remaining} of ${allowance.data.limit}`}
                hint={
                  allowance.data.remaining > 0
                    ? "This month"
                    : "Further ones need an admin's approval"
                }
                tone={allowance.data.remaining > 0 ? "ok" : "warn"}
              />
            )}
          </div>

          {row.totals.days_pending > 0 && (
            <Alert variant="destructive">
              <HelpCircle className="h-4 w-4" />
              <AlertTitle>
                {duration(row.totals.uncounted_minutes)} clocked on {row.totals.days_pending} day(s)
                is not counting
              </AlertTitle>
              <AlertDescription>
                {canApprove
                  ? "Those entries were clocked outside the employee's working pattern. The time is " +
                    "recorded; accept an entry below to make it count."
                  : "You clocked in on days outside your working pattern. The time is recorded — ask " +
                    "your manager to confirm what those days were."}
              </AlertDescription>
            </Alert>
          )}

          <TooltipProvider delayDuration={200}>
            {/*
             * The box scrolls, not the page. The period is free to be three
             * months, which is ninety-odd rows: left to grow, the headings
             * leave the screen on the way down and the total sits somewhere
             * below the fold. Capped, the days run under pinned headings with
             * the total always in view. A short period never reaches the cap,
             * so nothing scrolls that does not have to.
             */}
            <div className="bg-card min-h-0 overflow-auto rounded-md border">
              <table className="w-full border-collapse text-sm">
                {/* A pinned cell draws its bottom rule as an inset shadow: that
                    border is shared with the first body row under `border-collapse`,
                    so it scrolls away with the row and leaves the headings sitting
                    on the days with nothing between them. The rules between the
                    headings are shared with each other, and travel as they are. */}
                <thead>
                  <tr>
                    <th className={cn(STUCK_TOP, "w-28 border-r text-left")}>Day</th>
                    <th className={cn(STUCK_TOP, "border-r text-left")}>Clocked in and out</th>
                    <th className={cn(STUCK_TOP, "w-28 border-r text-left")}>First in, last out</th>
                    <th className={cn(STUCK_TOP, "w-24 border-r text-right")}>Hours</th>
                    <th className={cn(STUCK_TOP, "text-left")}>What the day was</th>
                  </tr>
                </thead>
                <tbody>
                  {row.days.map((day) => (
                    <TimesheetRowCells
                      key={day.date}
                      day={day}
                      contractedMinutes={row.contracted_minutes}
                      typeById={typeById}
                      asOf={asOf}
                      canApprove={canApprove}
                      onSettle={(entry) => setSettling({ employeeId, entry })}
                      correctable={canApprove || isSelf}
                      pendingCorrection={day.clocked.some((e) => pending.has(e.started_at))}
                      onCorrect={() => setAmending(day)}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-medium">
                    <td className={cn(STUCK_BOTTOM, "border-r")}>Total</td>
                    <td className={cn(STUCK_BOTTOM, "border-r text-xs font-normal")}>
                      {row.totals.entries} clock-in(s) across {row.totals.days_clocked} day(s)
                    </td>
                    <td
                      className={cn(
                        STUCK_BOTTOM,
                        "text-muted-foreground border-r text-xs font-normal",
                      )}
                    >
                      {row.work_day.start}–{row.work_day.end} contracted
                    </td>
                    {/* The one number the whole table adds up to, so it is set
                        apart from the figures above rather than reading as one
                        more row of them. */}
                    <td
                      className={cn(
                        STUCK_BOTTOM,
                        "border-r text-right text-base font-bold tabular-nums",
                      )}
                    >
                      {duration(row.totals.counted_minutes)}
                    </td>
                    <td className={cn(STUCK_BOTTOM, "text-muted-foreground text-xs font-normal")}>
                      of {duration(row.totals.contracted_minutes)} across {row.totals.working_days}{" "}
                      working day(s)
                      {row.totals.uncounted_minutes > 0 && (
                        <span className="text-destructive">
                          {" "}
                          · {duration(row.totals.uncounted_minutes)} not counting
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </TooltipProvider>
        </>
      )}

      {amending && (
        <AmendTimesDialog
          employeeId={employeeId}
          isSelf={isSelf}
          day={amending}
          pending={pending}
          onClose={() => setAmending(null)}
          onSaved={() => {
            setAmending(null);
            void qc.invalidateQueries({ queryKey: ["timesheet"] });
            void qc.invalidateQueries({ queryKey: ["attendance"] });
            void qc.invalidateQueries({ queryKey: ["time_amendments"] });
            void qc.invalidateQueries({ queryKey: ["amendment_allowance"] });
            void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
          }}
        />
      )}

      {settling && (
        <SettleEntryDialog
          employeeId={settling.employeeId}
          entry={settling.entry}
          onClose={() => setSettling(null)}
          onSettled={() => {
            setSettling(null);
            void qc.invalidateQueries({ queryKey: ["timesheet"] });
            void qc.invalidateQueries({ queryKey: ["attendance"] });
            void qc.invalidateQueries({ queryKey: ["clock"] });
            // An unsettled entry is one of the things the bell counts.
            void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
          }}
        />
      )}
    </div>
  );
}

/**
 * One date.
 *
 * A day nobody clocked is drawn as a dash rather than a zero: zero hours reads
 * as a measurement, and nothing was measured. What the day was comes from the
 * grid beside it, so a day of leave reads as leave instead of as missing hours.
 */
function TimesheetRowCells({
  day,
  contractedMinutes,
  typeById,
  asOf,
  canApprove,
  onSettle,
  correctable,
  pendingCorrection,
  onCorrect,
}: {
  day: TimesheetDay;
  contractedMinutes: number;
  typeById: Map<string, LeaveType>;
  asOf: string;
  canApprove: boolean;
  onSettle: (entry: TimesheetEntry) => void;
  /** Whether this caller may put this day's times right at all. */
  correctable: boolean;
  /** A correction to this day is already waiting on an admin. */
  pendingCorrection: boolean;
  onCorrect: () => void;
}) {
  /*
   * What the day was, as the marks the calendar draws rather than a sentence.
   *
   * It was a line of prose — "Morning: In the office · Afternoon: On leave" —
   * which is a column you have to read word by word to find the one day that is
   * not like the others. A badge in the status's own colour is the same fact
   * scannable, and the same colour the calendar and the legend already use for
   * it, so the three views agree.
   *
   * The softening is `effectiveStatus`'s, applied here too: a working day that
   * has not happened is still to come, not unanswered. This column said "Not
   * yet answered" against every remaining date of the month.
   */
  const marks = (): { key: string; label: string; half: string | null; style: string }[] => {
    const tone = (status: TileStatus) => STATUS_STYLE[status].tileClassName;

    const of = (half: DayHalf) => {
      const stored = day[`${half}_status`] as AttendanceStatus | null;
      if (!stored) return null;
      const status: TileStatus = stored === "unconfirmed" && day.date > asOf ? "upcoming" : stored;
      const typeId = day[`${half}_leave_type_id`];
      const type = typeId ? typeById.get(typeId) : undefined;
      return {
        status,
        label: status === "leave" ? (type?.name ?? "On leave") : STATUS_STYLE[status].label,
        style: tone(status),
      };
    };

    const am = of("am");
    const pm = of("pm");

    if (am && pm && am.label === pm.label) {
      return [{ key: "both", label: am.label, half: null, style: am.style }];
    }
    if (am || pm) {
      return (["am", "pm"] as const).map((half) => {
        const mark = half === "am" ? am : pm;
        return {
          key: half,
          half: half === "am" ? "AM" : "PM",
          label: mark?.label ?? STATUS_STYLE.unconfirmed.label,
          style: mark?.style ?? tone("unconfirmed"),
        };
      });
    }

    // Nothing stored at all. The calendar itself, in one badge.
    if (day.holiday) {
      return [
        { key: "holiday", label: STATUS_STYLE.holiday.label, half: null, style: tone("holiday") },
      ];
    }
    if (!day.working_day) {
      return [
        {
          key: "non_working",
          label: STATUS_STYLE.non_working.label,
          half: null,
          style: tone("non_working"),
        },
      ];
    }
    const pending: TileStatus = day.date > asOf ? "upcoming" : "unconfirmed";
    return [{ key: pending, label: STATUS_STYLE[pending].label, half: null, style: tone(pending) }];
  };

  // Only worth comparing on a day work was due and some was done. "Three hours
  // short" on a Sunday is noise, and on a day of leave it is wrong.
  const against =
    day.working_day && day.counted_minutes > 0 ? day.counted_minutes - contractedMinutes : 0;

  return (
    <tr
      className={cn(
        "hover:bg-muted/40 border-t transition-colors",
        !day.working_day && "bg-muted/30 text-muted-foreground",
      )}
    >
      {/*
       * Three fixed columns rather than words with spaces between them. Run
       * inline, `Tue 6 Oct` and `Mon 12 Oct` put their date in a different
       * place on each row, so a column of dates had no edge to read down. The
       * number is right-aligned in its own cell, which is what lines 6 up
       * under 12.
       */}
      <td className="border-r px-3 py-2 whitespace-nowrap">
        <span className="grid grid-cols-[1.9rem_1.1rem_auto] items-baseline gap-x-1 tabular-nums">
          <span className="text-muted-foreground text-xs">{WEEKDAY_INITIALS[day.weekday]}</span>
          <span className="text-right font-medium">{Number(day.date.slice(8))}</span>
          <span className="flex items-baseline gap-1">
            {MONTH_NAMES[Number(day.date.slice(5, 7)) - 1]?.slice(0, 3)}
            {day.locked && <Lock className="text-muted-foreground h-3 w-3 self-center" />}
          </span>
        </span>
      </td>

      <td className="border-r px-3 py-2">
        {day.clocked.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {day.clocked.map((entry) => (
              <EntryChip
                key={entry.started_at}
                entry={entry}
                canApprove={canApprove}
                onSettle={onSettle}
              />
            ))}
          </div>
        )}
      </td>

      <td className="border-r px-3 py-2 text-xs whitespace-nowrap tabular-nums">
        {day.first_in ? (
          <div className="flex items-center gap-1">
            <span>
              {timeOfDay(day.first_in)}–
              {day.open ? "now" : day.last_out ? timeOfDay(day.last_out) : "?"}
              {/* The bracket the day ran between, which is not the time worked:
                  nine to twelve and four to six is five hours across nine. */}
              {day.span_minutes > day.counted_minutes + day.uncounted_minutes && (
                <span className="text-muted-foreground"> ({duration(day.span_minutes)} span)</span>
              )}
            </span>

            {/*
             * Correcting the times sits here rather than in a menu, because
             * this is the column the mistake is visible in: somebody reading
             * down it for the day that says 10:31 instead of 08:00 should be
             * able to fix it from where they found it.
             *
             * A locked day has no button at all. Its hours are behind an issued
             * payslip, and offering a correction that would be refused is worse
             * than not offering one — the refusal arrives after the typing.
             */}
            {correctable && !day.locked && !day.open && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={onCorrect}
                    aria-label={`Correct the times on ${day.date}`}
                  >
                    {pendingCorrection ? (
                      <HelpCircle className="text-muted-foreground h-3.5 w-3.5" />
                    ) : (
                      <Pencil className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {pendingCorrection
                    ? "A correction to this day is waiting for an admin."
                    : "Correct the clocked times."}
                </TooltipContent>
              </Tooltip>
            )}

            {/* An amended time is a time somebody supplied, not one the clock
                saw, and the two should never read the same. */}
            {day.clocked.some((entry) => entry.amended_at) && (
              <span className="text-muted-foreground" title="A time on this day was corrected">
                ✎
              </span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      <td className="border-r px-3 py-2 text-right whitespace-nowrap tabular-nums">
        {day.counted_minutes > 0 ? (
          <span className="font-medium">{duration(day.counted_minutes)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
        {day.uncounted_minutes > 0 && (
          <div className="text-destructive text-xs">
            +{duration(day.uncounted_minutes)} not counting
          </div>
        )}
        {against !== 0 && (
          <div className="text-muted-foreground text-xs">
            {against > 0 ? `+${duration(against)}` : `−${duration(-against)}`}
          </div>
        )}
      </td>

      <td className="px-3 py-2 text-xs">
        <div className="flex flex-wrap items-center gap-1">
          {marks().map((mark) => (
            <span
              key={mark.key}
              className={cn(
                "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium",
                mark.style,
              )}
            >
              {mark.half && <span className="opacity-60">{mark.half}</span>}
              {mark.label}
            </span>
          ))}
        </div>
        {day.note && <div className="text-muted-foreground mt-1">{day.note}</div>}
      </td>
    </tr>
  );
}

/** `09:03` back to the stored instant, on the day the entry belongs to. */
function instantOn(date: string, hhmm: string): string {
  // What somebody typed into the dialog is the time on their own clock. Stamped
  // with a `Z` it was stored four hours early, so a corrected `09:00` came back
  // reading `05:00` and the correction looked like it had been ignored.
  return instantAt(date, hhmm);
}

/** The corrections still waiting on somebody, keyed by the entry they target. */
function pendingByEntry(rows: readonly TimeAmendmentRow[]): Map<string, TimeAmendmentRow> {
  const out = new Map<string, TimeAmendmentRow>();
  for (const row of rows) {
    if (row.status === "pending") out.set(row.entry_started_at, row);
  }
  return out;
}

/**
 * Correcting the times on one day.
 *
 * The case is ordinary and constant: somebody started at eight and clocked in
 * at half past, or left at six and remembered the next morning. The clock
 * recorded what it saw, which was wrong, and the only person who knows the
 * right answer is the one who was there — so this is theirs to fix, a fixed
 * number of times a month, and an admin's to approve after that.
 *
 * The allowance is stated before anything is typed rather than discovered by
 * being refused. "Your third of five" and "this one needs approval" are
 * different things to be doing, and somebody should know which one they are
 * doing while they decide whether it is worth it.
 *
 * One entry is one correction, because an entry is what gets rewritten. A day
 * with a lunch break in the middle of it has two, and both are editable here —
 * with the count said out loud, since fixing both halves of such a day spends
 * two of the month's allowance and nobody should find that out afterwards.
 */
function AmendTimesDialog({
  employeeId,
  isSelf,
  day,
  pending,
  onClose,
  onSaved,
}: {
  employeeId: string;
  /** Whether this is the caller's own timesheet, which is what the wording turns on. */
  isSelf: boolean;
  day: TimesheetDay;
  /** Corrections already waiting on an admin, so one is never asked for twice. */
  pending: Map<string, TimeAmendmentRow>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const closed = day.clocked.filter((entry) => !!entry.ended_at);

  /* `started_at` keys the draft because it keys the entry — and it is the one
     field a correction can change, so the draft holds the original as its key
     and the new time as a value. */
  const [draft, setDraft] = useState<Record<string, { in: string; out: string }>>(() =>
    Object.fromEntries(
      closed.map((entry) => [
        entry.started_at,
        { in: timeOfDay(entry.started_at), out: timeOfDay(entry.ended_at ?? entry.started_at) },
      ]),
    ),
  );
  const [reason, setReason] = useState("");

  const allowance = useQuery({
    queryKey: ["amendment_allowance", employeeId],
    queryFn: async () => {
      const result = await api.timeAmendments.allowance(employeeId);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });

  /** The entries whose times the form actually changes — one correction each. */
  const changed = closed.filter((entry) => {
    const edit = draft[entry.started_at];
    if (!edit || pending.has(entry.started_at)) return false;
    return edit.in !== timeOfDay(entry.started_at) || edit.out !== timeOfDay(entry.ended_at ?? "");
  });

  const remaining = allowance.data?.remaining ?? 0;
  const limit = allowance.data?.limit ?? 0;
  /* Beyond the allowance every one of them is a request, and a request without
     a reason gives an admin only the clock to go on — which is the thing being
     corrected. */
  const needsReason = changed.length > remaining;

  const problems = changed
    .map((entry) => {
      const edit = draft[entry.started_at]!;
      return amendmentProblem({
        date: day.date,
        startedAt: entry.started_at,
        endedAt: entry.ended_at,
        newStartedAt: edit.in === timeOfDay(entry.started_at) ? null : instantOn(day.date, edit.in),
        newEndedAt:
          edit.out === timeOfDay(entry.ended_at ?? "") ? null : instantOn(day.date, edit.out),
      });
    })
    .filter((problem): problem is string => !!problem);

  const save = useMutation({
    mutationFn: async () => {
      /*
       * One request per entry, in order, because one entry is one correction.
       * Sequential rather than in parallel so the allowance is spent in a
       * defined order — two corrections racing for the last one of the month
       * would otherwise both think they had it.
       */
      let applied = 0;
      let queued = 0;

      for (const entry of changed) {
        const edit = draft[entry.started_at]!;
        const result = await api.timeAmendments.create({
          employee_id: employeeId,
          entry_started_at: entry.started_at,
          new_started_at:
            edit.in === timeOfDay(entry.started_at) ? null : instantOn(day.date, edit.in),
          new_ended_at:
            edit.out === timeOfDay(entry.ended_at ?? "") ? null : instantOn(day.date, edit.out),
          reason: reason.trim() || null,
        });
        if (result.error) throw new Error(result.error.message);
        if (result.data?.applied) applied += 1;
        else queued += 1;
      }

      return { applied, queued };
    },
    onSuccess: ({ applied, queued }) => {
      const said = [
        applied > 0 ? `${applied} time(s) corrected` : null,
        queued > 0 ? `${queued} sent for approval` : null,
      ]
        .filter(Boolean)
        .join(", ");
      notify.success(said || "Nothing to change");
      onSaved();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Correct the times — {WEEKDAY_INITIALS[day.weekday]} {Number(day.date.slice(8))}{" "}
            {MONTH_NAMES[Number(day.date.slice(5, 7)) - 1]}
          </DialogTitle>
          <DialogDescription>
            The clock records when somebody pressed the button, which is not always when they
            started or stopped. Put the right times in and the day&rsquo;s hours are worked out
            again from them.
          </DialogDescription>
        </DialogHeader>

        {closed.length === 0 ? (
          <Alert>
            <AlertTitle>Nothing to correct on this day</AlertTitle>
            <AlertDescription>
              {day.open
                ? "This entry is still open. Clock out first, then correct the time."
                : "No clocked time was recorded, so there is no time to put right. A day nobody " +
                  "clocked is answered on the calendar instead."}
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            {/*
             * The allowance, stated plainly and before anything is typed. It is
             * the one thing that changes what pressing Save does.
             */}
            {allowance.isLoading ? (
              <p className="text-muted-foreground text-sm">Checking the allowance…</p>
            ) : (
              <Alert variant={remaining === 0 ? "destructive" : "default"}>
                <Info className="h-4 w-4" />
                <AlertTitle>
                  {remaining === 0
                    ? `All ${limit} of this month's corrections used`
                    : `${remaining} of ${limit} correction(s) left this month`}
                </AlertTitle>
                <AlertDescription>
                  {remaining === 0
                    ? isSelf
                      ? "This one goes to an admin to approve. Say why the time was wrong and it " +
                        "will be applied when they agree."
                      : "This employee has used their allowance, so a correction you make here is " +
                        "applied on your authority and spends none of it."
                    : isSelf
                      ? "A correction inside the allowance is applied straight away. After that an " +
                        "admin approves them."
                      : "A correction you make is applied straight away and spends none of their " +
                        "allowance."}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-3">
              {closed.map((entry, index) => {
                const edit = draft[entry.started_at] ?? { in: "", out: "" };
                const waiting = pending.get(entry.started_at);

                return (
                  <div key={entry.started_at} className="space-y-2 rounded-md border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium">
                        {closed.length > 1 ? `Entry ${index + 1}` : "Clocked"}
                        <span className="text-muted-foreground ml-2 font-normal tabular-nums">
                          {timeOfDay(entry.started_at)} → {timeOfDay(entry.ended_at ?? "")}
                        </span>
                      </p>
                      {entry.auto_closed && (
                        <Badge variant="outline" className="text-xs">
                          Closed automatically
                        </Badge>
                      )}
                    </div>

                    {waiting ? (
                      <p className="text-muted-foreground text-xs">
                        A correction to this entry is already waiting for an admin —{" "}
                        {describeAmendment({
                          prevStartedAt: waiting.prev_started_at,
                          prevEndedAt: waiting.prev_ended_at,
                          newStartedAt: waiting.new_started_at,
                          newEndedAt: waiting.new_ended_at,
                        })}
                        .
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor={`in-${entry.started_at}`} className="text-xs" required>
                            Clocked in
                          </Label>
                          <Input
                            id={`in-${entry.started_at}`}
                            type="time"
                            value={edit.in}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                [entry.started_at]: { ...edit, in: e.target.value },
                              }))
                            }
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`out-${entry.started_at}`} className="text-xs" required>
                            Clocked out
                          </Label>
                          <Input
                            id={`out-${entry.started_at}`}
                            type="time"
                            value={edit.out}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                [entry.started_at]: { ...edit, out: e.target.value },
                              }))
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="space-y-1">
              <Label htmlFor="amend-reason" required={needsReason}>
                Why the time was wrong{needsReason ? "" : " (optional)"}
              </Label>
              <Textarea
                id="amend-reason"
                rows={2}
                value={reason}
                placeholder="Started at eight, forgot to clock in until the first meeting."
                onChange={(e) => setReason(e.target.value)}
              />
              {needsReason && (
                <p className="text-muted-foreground text-xs">
                  Needed, because an admin has to decide this one.
                </p>
              )}
            </div>

            {problems.length > 0 && (
              <Alert variant="destructive">
                <AlertDescription>{problems[0]}</AlertDescription>
              </Alert>
            )}

            {changed.length > 1 && (
              <p className="text-muted-foreground text-xs">
                {changed.length} entries changed, which is {changed.length} of the month&rsquo;s
                corrections.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={
              save.isPending ||
              changed.length === 0 ||
              problems.length > 0 ||
              (needsReason && !reason.trim())
            }
          >
            {needsReason ? "Send for approval" : "Correct the times"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Corrections to clocked times — what is waiting, and what has been done.
 *
 * Its own view rather than a notification somewhere, because the two audiences
 * need the same list for opposite reasons: an employee checks whether the fix
 * they asked for has gone through, and an admin decides it. Both want it beside
 * the timesheet it is about.
 *
 * Applied corrections stay listed. They are the record of who changed a clocked
 * time and why — which is the whole reason a correction is a row rather than an
 * edit — and a list that showed only the open ones would hide it.
 */
function TimeCorrections({
  employeeId,
  canApprove,
  canChoose,
}: {
  employeeId: string;
  canApprove: boolean;
  canChoose: boolean;
}) {
  const qc = useQueryClient();
  // Unset until somebody picks a tab: it opens on the queue when there is one,
  // and on the record when nothing is waiting, rather than on an empty table.
  const [picked, setQueue] = useState<"waiting" | "decided" | null>(null);
  const [person, setPerson] = useState("all");
  // Paged in the browser: the list is read whole, so the pager slices it. A new
  // tab or person starts again at page 1.
  const pager = usePagination({ resetKey: { picked, person } });

  /*
   * Everything the caller may see when they can decide, so a manager opens this
   * and finds the queue rather than having to pick each person in turn. For
   * everybody else it is their own, which is all the server would return anyway.
   */
  const scope = canApprove ? undefined : employeeId;

  const rows = useQuery({
    queryKey: ["time_amendments", scope ?? "all"],
    queryFn: async () => {
      const result = await api.timeAmendments.list(scope ? { employeeId: scope } : {});
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
  });

  const decide = useMutation({
    mutationFn: async (args: {
      id: string;
      decision: "approve" | "reject" | "cancel";
      note?: string;
    }) => {
      const result = await api.timeAmendments.decide(args.id, args.decision, args.note);
      if (result.error) throw new Error(result.error.message);
      return args.decision;
    },
    onSuccess: (decision) => {
      notify.success(
        decision === "approve"
          ? "Approved. The timesheet now reads the corrected times."
          : decision === "reject"
            ? "Rejected. The clocked times are unchanged."
            : "Withdrawn.",
      );
      void qc.invalidateQueries({ queryKey: ["time_amendments"] });
      void qc.invalidateQueries({ queryKey: ["timesheet"] });
      void qc.invalidateQueries({ queryKey: ["attendance"] });
      void qc.invalidateQueries({ queryKey: ["amendment_allowance"] });
      /* The bell counts pending corrections, so deciding one has to reach it —
         otherwise the count sits behind the list that just cleared the row. */
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
    onError: (error: Error) => notify.error(error.message),
  });

  if (rows.isLoading) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (rows.error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{(rows.error as Error).message}</AlertDescription>
      </Alert>
    );
  }

  const all = rows.data ?? [];
  const waiting = all.filter((row) => row.status === "pending");
  const settled = all.filter((row) => row.status !== "pending");

  if (all.length === 0) {
    return (
      <Alert>
        <AlertTitle>No corrections</AlertTitle>
        <AlertDescription>
          Nobody has corrected a clocked time yet. A correction is made from the Hours view, on the
          day it belongs to.
        </AlertDescription>
      </Alert>
    );
  }

  const queue = picked ?? (waiting.length > 0 ? "waiting" : "decided");
  const people = [
    ...new Set(all.map((row) => row.employee_name).filter((name): name is string => !!name)),
  ].sort((a, b) => a.localeCompare(b));
  const shown = (from: TimeAmendmentRow[]) =>
    person === "all" ? from : from.filter((row) => row.employee_name === person);
  const matching = shown(queue === "waiting" ? waiting : settled);
  const rowsShown = matching.slice(pager.offset, pager.offset + pager.pageSize);
  const columns = (canChoose ? 7 : 6) as number;

  const actions = (row: TimeAmendmentRow) =>
    row.status !== "pending" ? null : canApprove ? (
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          onClick={() => decide.mutate({ id: row.id, decision: "approve" })}
          disabled={decide.isPending}
        >
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => decide.mutate({ id: row.id, decision: "reject" })}
          disabled={decide.isPending}
        >
          Reject
        </Button>
      </div>
    ) : (
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          onClick={() => decide.mutate({ id: row.id, decision: "cancel" })}
          disabled={decide.isPending}
        >
          Withdraw
        </Button>
      </div>
    );

  /*
   * A table, split by whether it still needs a decision: the queue is what an
   * approver acts on, and the decided ones are the record. The person filter is
   * built from the names on the rows, since this list is already whole.
   */
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Tabs value={queue} onValueChange={(v) => setQueue(v as "waiting" | "decided")}>
          <TabsList>
            <TabsTrigger value="waiting" className="gap-2">
              Awaiting decision
              {waiting.length > 0 && (
                <Badge variant="secondary" className="bg-warning text-warning-foreground">
                  {waiting.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="decided">Decided</TabsTrigger>
          </TabsList>
        </Tabs>
        {canChoose && people.length > 1 && (
          <FilterSelect
            value={person}
            onChange={setPerson}
            className="w-56"
            options={[
              { value: "all", label: "Everybody" },
              ...people.map((name) => ({ value: name, label: name })),
            ]}
          />
        )}
      </div>

      <div className="bg-card flex min-h-0 flex-col overflow-hidden rounded-md border">
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              {canChoose && <TableHead>Person</TableHead>}
              <TableHead className="w-28">Date</TableHead>
              <TableHead>Change</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="w-44">Asked</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rowsShown.length === 0 ? (
              <TableMessageRow colSpan={columns}>
                {queue === "waiting"
                  ? "Nothing is waiting for a decision."
                  : "Nothing decided yet."}
              </TableMessageRow>
            ) : (
              rowsShown.map((row) => (
                <TableRow key={row.id}>
                  {canChoose && (
                    <TableCell className="whitespace-nowrap font-medium">
                      {row.employee_name ?? "—"}
                    </TableCell>
                  )}
                  <TableCell className="whitespace-nowrap tabular-nums">{row.date}</TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {describeAmendment({
                      prevStartedAt: row.prev_started_at,
                      prevEndedAt: row.prev_ended_at,
                      newStartedAt: row.new_started_at,
                      newEndedAt: row.new_ended_at,
                    })}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-xs">
                    <Truncated text={row.reason || "—"} />
                    {row.decision_note && (
                      <Truncated text={`Note: ${row.decision_note}`} className="text-xs" />
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    <div className="whitespace-nowrap tabular-nums">
                      {new Date(row.created_at).toLocaleString()}
                    </div>
                    {row.counts_against_allowance
                      ? "Counted against the allowance"
                      : row.status === "applied"
                        ? "On an admin's authority"
                        : null}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        row.status === "applied"
                          ? "default"
                          : row.status === "pending"
                            ? "outline"
                            : "secondary"
                      }
                    >
                      {AMENDMENT_LABELS[row.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>{actions(row)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination pagination={pager} total={matching.length} unit="time edit" />
      </div>
    </div>
  );
}

/** What each status is called on screen. */
const AMENDMENT_LABELS: Record<TimeAmendmentStatus, string> = {
  pending: "Waiting",
  applied: "Applied",
  rejected: "Rejected",
  cancelled: "Withdrawn",
};

/**
 * One clock-in and clock-out.
 *
 * What is on the row is the bracket and whether it counted, which is the only
 * part anybody has to act on. Everything else about the entry — an automatic
 * clock-out, a manager's answer — is in the tooltip.
 */
function EntryChip({
  entry,
  canApprove,
  onSettle,
}: {
  entry: TimesheetEntry;
  canApprove: boolean;
  onSettle: (entry: TimesheetEntry) => void;
}) {
  const settleable = canApprove && !entry.counted && !!entry.ended_at;

  const chip = (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs tabular-nums",
        entry.counted
          ? "bg-background"
          : "border-orange-400 bg-orange-50 text-orange-900 dark:bg-orange-950/40 dark:text-orange-200",
        settleable && "cursor-pointer hover:ring-2 hover:ring-ring",
      )}
    >
      {timeOfDay(entry.started_at)} → {entry.ended_at ? timeOfDay(entry.ended_at) : "now"}
      <span className="text-muted-foreground">{duration(entry.minutes)}</span>
    </span>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {settleable ? (
          <button type="button" onClick={() => onSettle(entry)}>
            {chip}
          </button>
        ) : (
          chip
        )}
      </TooltipTrigger>
      <TooltipContent className="max-w-64">
        <p className="font-medium">
          {timeOfDay(entry.started_at)} →{" "}
          {entry.ended_at ? timeOfDay(entry.ended_at) : "still open"}
        </p>
        {!entry.counted && (
          <p className="mt-1">
            {entry.status === "rejected"
              ? "Recorded as not worked."
              : "Not counting until somebody says what this day was."}
          </p>
        )}
        {entry.auto_closed && (
          <p className="text-muted-foreground mt-1">
            Clocked out automatically at the end of the working day.
          </p>
        )}
        {entry.resolved_status && (
          <p className="text-muted-foreground mt-1">
            Accepted as {STATUS_STYLE[entry.resolved_status].label.toLowerCase()}.
          </p>
        )}
        {settleable && <p className="mt-1 font-medium">Click to settle it.</p>}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * A manager settling one clocked entry.
 *
 * The counterpart to the clock never being refused. An entry clocked outside
 * somebody's working pattern counts for nothing until somebody says what it
 * was, which has to be sayable — otherwise the strictness is not a question
 * waiting for an answer, it is just lost hours.
 *
 * Naming the status matters: the clock cannot tell a day at home from a day at
 * a client's office, and the grid should read as what happened rather than as
 * the default a clocked day falls back to.
 */
function SettleEntryDialog({
  employeeId,
  entry,
  onClose,
  onSettled,
}: {
  employeeId: string;
  entry: TimesheetEntry;
  onClose: () => void;
  onSettled: () => void;
}) {
  const [status, setStatus] = useState<AttendanceStatus>("wfh");
  const [note, setNote] = useState(entry.note ?? "");

  const settle = useMutation({
    mutationFn: async (decision: "resolve" | "reject") => {
      const result = await api.attendance.settleEntry(decision, employeeId, entry.started_at, {
        status,
        note: note.trim() || null,
      });
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (data) => {
      notify.success(data?.message ?? "Entry settled");
      onSettled();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const offered: AttendanceStatus[] = ["office", ...REMOTE_WORKING_STATUSES];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {entry.date} — {timeOfDay(entry.started_at)} to{" "}
            {entry.ended_at ? timeOfDay(entry.ended_at) : "now"}
          </DialogTitle>
          <DialogDescription>
            {duration(entry.minutes)} was clocked on a day outside this person's working pattern —
            so it counts for nothing until you say what it was.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label>Count it as</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as AttendanceStatus)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {offered.map((option) => (
                <SelectItem key={option} value={option}>
                  {STATUS_STYLE[option].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="settle-note">Note</Label>
          <Input
            id="settle-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional — what they were doing"
          />
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="outline"
            className="text-destructive"
            disabled={settle.isPending}
            onClick={() => settle.mutate("reject")}
          >
            Not worked
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={settle.isPending} onClick={() => settle.mutate("resolve")}>
              Accept the time
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
